import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { ConversationSummary } from '../services/conversationHistoryService';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ThumbsUp, ThumbsDown, MessageSquare, Lightbulb, Bot, AlertTriangle, Home, Info, Target, TrendingUp, Scale, Shuffle, Unlock, Tag, Sparkles, Folder, Zap, Filter, Search, Link, ArrowRight, CheckCircle, Database, ChevronDown, Calendar, type LucideIcon } from 'lucide-react';
import { Message, Agent, Citation, User, ToolCallDetail } from '../types';
import { ToolCall } from '../hooks/useChat';
import { useFeedback } from '../hooks/useFeedback';
import QueryDetailsSection from '../components/QueryDetailsSection';
import { useAuth } from '../hooks/useAuth';
import { useProductCardLoading } from '../hooks/useProductCardLoading';
import RegenerateModal from '../components/RegenerateModal';
import { useMonitoring } from '../services/monitoring';
import { useApplicationInsights } from '../services/applicationInsights';
import type { UserProfile } from '../hooks/useUserProfile';

// import A2ARoutingAvatar from '../components/A2ARoutingAvatar'; // Routing UI removed - showing specialist agent directly
import TopNavbar from '../components/TopNavbar';
import StreamingText, { dedupeImageMarkdown } from '../components/StreamingText';
import ScaledImage from '../components/ScaledImage';
import ImageScaleSettings from '../components/ImageScaleSettings';
import { useImageScale } from '../hooks/useImageScale';
import { useFastMode } from '../hooks/useFastMode';
import { cancelAllImagePreloads } from '../hooks/useImagePreloader';
// import RoutingBadge from '../components/RoutingBadge'; // Routing UI removed - showing specialist agent directly
import FeedbackModal from '../components/FeedbackModal';
import FeedbackRequestModal from '../components/FeedbackRequestModal';
import AgentIcon from '../components/AgentIcon';
import AgentWhatsNewEditor from '../components/AgentWhatsNewEditor';
import { applyWhatsNew } from '../utils/whatsNew';
import { useFeedbackRequest } from '../hooks/useFeedbackRequest';
import { FilterAlternatives } from '../components/FilterAlternatives';

import InputBox from '../components/InputBox';
import './ChatInterface.css';
import { ProductCard, ProductDisplay, type ProductDisplayData } from '../components/products';
import ProductCollaboratorView, {
  type ProductCollaboratorViewHandle,
} from '../components/products/ProductCollaboratorView';
import type { ProductSearchlightMode } from '../components/products/ProductSearchlightModeToggle';
import { sanitizeUrl, getProxiedImageUrl, getFallbackImageUrl } from '../components/products/utils';
import { isFeatureEnabled } from '../config/appConfig';
import { SAMPLE_QUESTIONS, SampleQuestion } from '../utils/sampleQuestions';
import { resolveProductDisplayKey } from '../utils/productAnchors';

// Whitelist of "UI" emojis we want to render as Lucide icons instead of raw
// Unicode so callout / tip / warning glyphs in agent text (especially the
// Product Searchlight intro message) look consistent with the rest of the UI.
// Order matters: variation-selector forms (e.g. "⚠️" = U+26A0 + U+FE0F) must be
// listed before the bare codepoint so the regex matches the longer pattern.
const UI_EMOJI_ICON_MAP: Array<{
  pattern: string;
  Icon: LucideIcon;
  colorClass: string;
  ariaLabel: string;
}> = [
  { pattern: '⚠️', Icon: AlertTriangle, colorClass: 'text-amber-600',  ariaLabel: 'Warning' },
  { pattern: '⚠',  Icon: AlertTriangle, colorClass: 'text-amber-600',  ariaLabel: 'Warning' },
  { pattern: '📅', Icon: Calendar,      colorClass: 'text-slate-500',  ariaLabel: 'Date' },
  { pattern: '💡', Icon: Lightbulb,     colorClass: 'text-yellow-500', ariaLabel: 'Tip' },
  { pattern: 'ℹ️', Icon: Info,           colorClass: 'text-blue-500',   ariaLabel: 'Info' },
  { pattern: 'ℹ',  Icon: Info,           colorClass: 'text-blue-500',   ariaLabel: 'Info' },
];

const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const UI_EMOJI_RE = new RegExp(
  `(${[...UI_EMOJI_ICON_MAP]
    .sort((a, b) => b.pattern.length - a.pattern.length)
    .map((e) => escapeRegex(e.pattern))
    .join('|')})\\s?`,
  'g'
);

/**
 * Scan a string and swap any whitelisted UI emoji (⚠️ 📅 💡 ℹ️) for an inline
 * Lucide icon. Returns an array of React nodes (mix of strings and icons) so it
 * can be spliced into a parent's children list.
 */
const replaceUiEmojis = (text: string): React.ReactNode[] => {
  if (!text) return [text];
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  UI_EMOJI_RE.lastIndex = 0;
  while ((match = UI_EMOJI_RE.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const found = UI_EMOJI_ICON_MAP.find((e) => e.pattern === match![1]);
    if (found) {
      const Icon = found.Icon;
      parts.push(
        <Icon
          key={`uiemo-${key++}-${match.index}`}
          size={16}
          className={`inline-block align-text-bottom mr-1 ${found.colorClass}`}
          aria-label={found.ariaLabel}
        />
      );
    } else {
      parts.push(match[0]);
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length ? parts : [text];
};

/**
 * React-Markdown component overrides that swap whitelisted UI emojis (⚠️/📅/💡/ℹ️)
 * for Lucide icons inside the paragraph, blockquote, list-item, and strong
 * elements. Used by the welcome-message renderer for agent intro messages.
 */
const buildEmojiAwareMarkdownComponents = () => {
  const processChildren = (children: React.ReactNode): React.ReactNode[] =>
    React.Children.toArray(children).flatMap((child) =>
      typeof child === 'string' ? replaceUiEmojis(child) : [child]
    );

  return {
    p: ({ node, children, ...props }: any) => (
      <p {...props}>{processChildren(children)}</p>
    ),
    li: ({ node, children, ...props }: any) => (
      <li {...props}>{processChildren(children)}</li>
    ),
    strong: ({ node, children, ...props }: any) => (
      <strong {...props}>{processChildren(children)}</strong>
    ),
    em: ({ node, children, ...props }: any) => (
      <em {...props}>{processChildren(children)}</em>
    ),
    a: ({ node, href, children, ...props }: any) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-teal-600 dark:text-teal-400 hover:underline"
        {...props}
      >
        {processChildren(children)}
      </a>
    ),
  };
};

// Icon mapping for search summary events
// Generic "thinking" status placeholders that carry no specific info — these are
// shown as the sleek breathing-dots indicator only (no robotic text). Informative
// statuses (e.g. Searching for "FR jackets"…) keep their text next to the dots.
const GENERIC_STATUS_RE = /^(processing|searching|considering your question|reviewing materials|gathering information|searching\s+(knowledge base|product catalog|policy documents|pwms documentation)|searching the knowledge bases?)\b[\s.…]*$/i;
const isGenericStatus = (content?: string): boolean => {
  const text = (content || '').trim();
  return text.length === 0 || GENERIC_STATUS_RE.test(text);
};

// Normalize an informative status into a clean label that sits BEFORE the dots:
// drop leading status icons/emoji (🔍 etc.) and any trailing ellipsis so the
// animated dots are the only "…" shown (no doubled ellipses).
const cleanStatusLabel = (content?: string): string => {
  return (content || '')
    .trim()
    // leading emoji / pictographs / symbols + whitespace
    .replace(/^[\u{1F000}-\u{1FAFF}\u{2190}-\u{27BF}\u{2B00}-\u{2BFF}\uFE0F\u200D\s]+/u, '')
    // trailing ellipsis (… or ...), preserving a closing quote if present
    .replace(/[.\u2026]+(\s*["'»”’]?)\s*$/u, '$1')
    .trim();
};

const getSearchEventIcon = (iconName: string): LucideIcon | null => {
  const iconMap: Record<string, LucideIcon> = {
    'target': Target,
    'trending-up': TrendingUp,
    'scale': Scale,
    'shuffle': Shuffle,
    'unlock': Unlock,
    'tag': Tag,
    'sparkles': Sparkles,
    'folder': Folder,
    'lightbulb': Lightbulb,
    'zap': Zap,
    'filter': Filter,
    'search': Search,
    'link': Link,
    'balance-scale': Scale,
    'arrow-right': ArrowRight,
    'check-circle': CheckCircle,
    'database': Database,
    'info': Info,
  };
  return iconMap[iconName] || null;
};

interface ChatInterfaceProps {
  agent: Agent | null;
  user: User;
  messages: Message[];
  onSendMessage: (content: string, context?: any) => void;
  onBackToAgents: () => void;
  isAgentTyping?: boolean;
  isStreaming?: boolean;
  currentStreamingMessage?: string;
  toolCalls?: ToolCall[];
  currentToolCall?: string | null;
  onClearChat?: () => void;
  onMessagesUpdate?: (messages: Message[]) => void;
  onRefreshAgents?: () => void;
  sessionId?: string;
  userId?: string;
  onRegenerateMessage?: (messageId: string, agent: Agent, messages: Message[], onUpdate: (messages: Message[]) => void, streamingPreference?: boolean, additionalContext?: string) => Promise<void>;
  chatError?: string | null;
  onClearError?: () => void;
  // Streaming toggle props
  isStreamingEnabled?: boolean;
  onToggleStreaming?: (enabled: boolean) => void;

  // Agent selection method props
  isAgentSelectedViaCard?: boolean;
  // Landing page props
  LandingPageComponent?: React.ComponentType<any>;
  onLandingPageQuestion?: (question: string, targetAgentId?: string) => Promise<void>;
  /** Shown as "Recent with Scout" on the landing page. */
  recentConversations?: ConversationSummary[];
  onOpenConversation?: (conversationId: string) => void;
  // Mobile layout props
  onToggleSidebar?: () => void;
  isSidebarOpen?: boolean;
  // Product display events keyed by anchor_id
  productDisplays?: Record<string, any[]>;
  // Alternative displays keyed by anchor_id for [ALTERNATIVES: anchor_id] replacement
  alternativeDisplays?: Record<string, any>;
  // GENERIC: Callback for clickable filter alternatives (any agent)
  handleAlternativeSelect?: (filters: Record<string, any>, description: string) => void;
  availableAgents: Agent[];
  // User profile for personalization
  userProfile?: UserProfile | null;
}



// Memoized message component to prevent unnecessary re-renders
const ChatMessage = React.memo<{
  message: Message;
  agent: Agent;
  user: User;
  isAnyAgentActivity: boolean;
  isStreaming: boolean;
  currentStreamingMessage: string;
  onMessagesUpdate?: (messages: Message[]) => void;
  onRegenerateClick: (messageId: string) => void;
  onCopyMessage: (messageId: string) => void;
  onSuggestionClick: (suggestion: string) => void;
  onCitationClick: (citation: Citation) => void;
  onFeedbackSubmit: (
    messageId: string,
    feedback: 'positive' | 'negative',
    agentName: string,
    userMessage: string,
    agentMessage: string,
    feedbackTags?: string[],
    feedbackNotes?: string,
    allowUpdate?: boolean
  ) => Promise<void>;
  messages: Message[];
  isWelcomeMessage?: boolean;
  canEditWhatsNew?: boolean;
  onEditWhatsNew?: () => void;
  showConversationStarters?: boolean;
  messageFeedback?: { positive: boolean; negative: boolean };
  isSourcesSectionExpanded: boolean;
  onToggleSourcesSection: () => void;
  setMessageLoading: (messageId: string) => void;
  setMessageLoaded: (messageId: string) => void;
  thumbsAnimating?: boolean;
  glowingThumb?: 'up' | 'down' | null;
  isMessageLoading: (messageId: string) => boolean;
  productDisplays?: Record<string, any[]>;
  alternativeDisplays?: Record<string, any>;
  onSendMessage: (content: string, context?: any) => void;
  imageMaxWidth?: string;
  imageScale?: number; // 0-100 scale for product cards
}>(({
  message,
  agent,
  user,
  isAnyAgentActivity,
  isStreaming,
  currentStreamingMessage,
  onMessagesUpdate,
  onRegenerateClick,
  onCopyMessage,
  onSuggestionClick,
  onCitationClick,
  onFeedbackSubmit,
  messages,
  isWelcomeMessage = false,
  canEditWhatsNew = false,
  onEditWhatsNew,
  showConversationStarters = false,
  messageFeedback,
  isSourcesSectionExpanded,
  onToggleSourcesSection,
  setMessageLoading,
  setMessageLoaded,
  thumbsAnimating = false,
  glowingThumb = null,
  isMessageLoading,
  productDisplays,
  alternativeDisplays,
  onSendMessage,
  imageMaxWidth = '280px',
  imageScale = 50
}) => {


  const isUserMessage = message.sender === 'user';
  const isAgentMessage = message.sender === 'agent';
  const isSystemMessage = message.sender === 'system';

  // CRITICAL: Use message-specific product displays to prevent retroactive updates
  // Prefer message.productDisplays over global productDisplays
  // MUST BE DEFINED EARLY - used in streaming render logging
  // FIX: Check if message.productDisplays has any keys (not just truthy check)
  const effectiveProductDisplays = (message.productDisplays && Object.keys(message.productDisplays).length > 0) 
    ? message.productDisplays 
    : productDisplays;
  
  // DEBUG: Log product display resolution for completed messages
  if (message.isCompleted && (message.content.includes('[PRODUCT_CARDS:') || message.content.includes('[PRODUCTS:'))) {
    console.log('🔍 PRODUCT_DISPLAY_RESOLUTION:', {
      messageId: message.id,
      hasMessageProductDisplays: !!message.productDisplays,
      messageProductDisplaysKeys: message.productDisplays ? Object.keys(message.productDisplays) : [],
      messageProductDisplaysCount: message.productDisplays ? Object.keys(message.productDisplays).length : 0,
      globalProductDisplaysKeys: productDisplays ? Object.keys(productDisplays) : [],
      globalProductDisplaysCount: productDisplays ? Object.keys(productDisplays).length : 0,
      effectiveProductDisplaysKeys: effectiveProductDisplays ? Object.keys(effectiveProductDisplays) : [],
      effectiveProductDisplaysCount: effectiveProductDisplays ? Object.keys(effectiveProductDisplays).length : 0,
      willRenderCards: !!(effectiveProductDisplays && Object.keys(effectiveProductDisplays).length > 0)
    });
  }

  // Check if this message is currently being streamed
  const isCurrentlyStreaming = isStreaming && message.id === 'streaming-message';
  if (isCurrentlyStreaming) {
    console.log('🎬 STREAMING_RENDER', {
      messageId: message.id,
      contentLen: (currentStreamingMessage || message.content)?.length || 0,
      hasAnchors: (currentStreamingMessage || message.content || '').match(/\[(?:PRODUCTS|PRODUCT_CARDS):/),
      productDisplaysKeys: effectiveProductDisplays ? Object.keys(effectiveProductDisplays).length : 0
    });
  }

  // Product-agent specific helpers
  // Check if this is a product-related agent (by ID or by having product displays)
  const hasProductDisplayData = !!(effectiveProductDisplays && Object.keys(effectiveProductDisplays).length > 0);
  const isProductAgentId = !!(agent && (
    agent.id === 'product' ||
    agent.id === 'product_finder_assistant' ||
    (typeof agent.id === 'string' && agent.id.toLowerCase().includes('product'))
  )) || hasProductDisplayData; // Also enable for any message with product displays
  
  // Debug agent ID issue
  console.log('🔍 AGENT_ID_CHECK:', { agentId: agent?.id, isProductAgentId, hasProductDisplayData });
  
  // Flatten products available for this message (from productDisplays map)
  // Handles both legacy format (arrays) and new format ({products, heading, facets})
  const productListForMessage = React.useMemo(() => {
    const list: any[] = [];
    if (effectiveProductDisplays) {
      Object.values(effectiveProductDisplays).forEach((displayData: any) => {
        // Handle new format: {products, heading, facets}
        const products = Array.isArray(displayData) ? displayData : (displayData?.products || []);
        products.forEach((p: any) => list.push(p));
      });
    }
    return list;
  }, [effectiveProductDisplays]);

  // Build mapping from tokens (name/sku) to scroll target ids, anchor IDs, and a filtered set of style-code tokens
  const { productScrollIdByToken, styleCodeTokens, styleCodeToAnchor } = React.useMemo(() => {
    const map = new Map<string, string>();
    const codeToAnchor = new Map<string, string>(); // Maps style code -> anchor (display ID)
    const styleTokens: string[] = [];
    const seen = new Set<string>();
    const normalizeSku = (s: any) => (s ? String(s).toUpperCase().replace(/[^A-Z0-9]/g, '') : '');
    const slugify = (s: any) => (s ? String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') : '');
    const maybePushStyleToken = (token?: string, anchor?: string) => {
      if (!token) return;
      if (token.length < 3) return;
      styleTokens.push(token);
      if (anchor) codeToAnchor.set(token, anchor);
    };

    // Process each display separately to track which anchor each product belongs to
    if (effectiveProductDisplays) {
      Object.entries(effectiveProductDisplays).forEach(([anchor, displayData]: [string, any]) => {
        const products = Array.isArray(displayData) ? displayData : (displayData?.products || []);
        products.forEach((p: any) => {
          const baseSku = normalizeSku(p.base_sku);
          const partialSku = normalizeSku(p.partialSku_OPIM);
          const styleCode = normalizeSku(p.attributes?.style_code);
          const name = p.name || '';
          const nameSlug = slugify(name);
          // MUST match ProductDisplay.tsx: baseSku || styleCode || partial || nameSlug || 'product'
          const scrollId = `product-card-${baseSku || styleCode || partialSku || nameSlug || 'product'}`;

          if (baseSku && !seen.has(baseSku)) { map.set(baseSku, scrollId); seen.add(baseSku); maybePushStyleToken(baseSku, anchor); }
          if (partialSku && !seen.has(partialSku)) { map.set(partialSku, scrollId); seen.add(partialSku); maybePushStyleToken(partialSku, anchor); }
          if (styleCode && !seen.has(styleCode)) { map.set(styleCode, scrollId); seen.add(styleCode); maybePushStyleToken(styleCode, anchor); }

          // Do not add name tokens to styleTokens; keep only true codes for linking
          if (name) {
            const lower = name.toLowerCase();
            if (!seen.has(lower)) { map.set(name, scrollId); map.set(lower, scrollId); seen.add(lower); }
          }

          if (Array.isArray(p.color_variants)) {
            p.color_variants.forEach((v: any) => {
              const vSku = normalizeSku(v.sku || v.variant_sku);
              if (vSku && !seen.has(vSku)) { map.set(vSku, scrollId); seen.add(vSku); maybePushStyleToken(vSku, anchor); }
            });
          }
        });
      });
    }

    console.log('🔗 STYLE_CODE_TOKENS:', {
      count: styleTokens.length,
      tokens: styleTokens.slice(0, 10),
      mapSize: map.size,
      hasEffectiveDisplays: !!effectiveProductDisplays,
      displayKeys: effectiveProductDisplays ? Object.keys(effectiveProductDisplays) : []
    });

    return {
      productScrollIdByToken: map,
      styleCodeTokens: styleTokens,
      styleCodeToAnchor: codeToAnchor
    };
  }, [effectiveProductDisplays]);

  // Dynamic regex of tokens (only real style/sku codes, not arbitrary numbers or names)
  const productTokensRegex = React.useMemo(() => {
    if (!styleCodeTokens || styleCodeTokens.length === 0) {
      console.log('🔗 CLICK-TO-HIGHLIGHT: No style code tokens found', { styleCodeTokens });
      return null as RegExp | null;
    }
    const tokens = [...styleCodeTokens].sort((a, b) => b.length - a.length);
    const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    console.log('🔗 CLICK-TO-HIGHLIGHT: Built regex with', tokens.length, 'tokens:', tokens.slice(0, 10));
    return new RegExp(`(${tokens.map(escape).join('|')})`, 'gi');
  }, [styleCodeTokens]);

  // Track selected style codes PER DISPLAY (keyed by anchor)
  const [selectedStyleCodesByAnchor, setSelectedStyleCodesByAnchor] = React.useState<Record<string, string[]>>({});

  // Whether the user has expanded the "Show full welcome message" disclosure on
  // a returning-user welcome card. Only meaningful when the welcome message
  // carries `metadata.fullWelcomeMessage` (set by App.tsx for users who have
  // already seen the current version of the agent).
  const [isFullWelcomeExpanded, setIsFullWelcomeExpanded] = React.useState(false);
  // Whether the welcome card's "Try asking:" panel has been expanded to show
  // the full list of sample questions. By default the panel renders only the
  // first 3 and tucks the rest behind a "Show more" toggle.
  const [areMoreStartersOpen, setAreMoreStartersOpen] = React.useState(false);
  const VISIBLE_STARTERS_COUNT = 3;

  React.useEffect(() => {
    setSelectedStyleCodesByAnchor({});
  }, [message.id, effectiveProductDisplays]);

  const toggleStyleCode = React.useCallback((code: string) => {
    const normalized = code.toUpperCase().replace(/[^A-Z0-9]/g, '');
    const anchor = styleCodeToAnchor.get(normalized);
    if (!anchor) return; // Unknown anchor, do nothing
    
    setSelectedStyleCodesByAnchor(prev => {
      const currentCodes = prev[anchor] || [];
      const newCodes = currentCodes.includes(normalized)
        ? currentCodes.filter(c => c !== normalized)
        : [...currentCodes, normalized];
      return { ...prev, [anchor]: newCodes };
    });
  }, [styleCodeToAnchor]);
  
  // Helper to get selected codes for a specific anchor
  const getSelectedCodesForAnchor = React.useCallback((anchor: string): string[] => {
    return selectedStyleCodesByAnchor[anchor] || [];
  }, [selectedStyleCodesByAnchor]);
  
  // Helper to set selected codes for a specific anchor
  const setSelectedCodesForAnchor = React.useCallback((anchor: string, codes: string[]) => {
    setSelectedStyleCodesByAnchor(prev => ({ ...prev, [anchor]: codes }));
  }, []);

  // Smooth scroll helper
  const scrollToProduct = React.useCallback((token: string) => {
    const normalized = token.toUpperCase().replace(/[^A-Z0-9]/g, '');
    const candidateId = productScrollIdByToken.get(token)
      || productScrollIdByToken.get(normalized)
      || productScrollIdByToken.get(token.toLowerCase())
      || `product-card-${normalized}`;
    
    // Debug: Show what we're looking for
    const mapHasToken = productScrollIdByToken.has(token);
    const mapHasNormalized = productScrollIdByToken.has(normalized);
    console.log('🎯 SCROLL_TO_PRODUCT:', {
      token,
      normalized,
      candidateId,
      mapHasToken,
      mapHasNormalized,
      mapKeys: Array.from(productScrollIdByToken.keys()).slice(0, 15)
    });

    const attemptScroll = () => {
      const el = document.getElementById(candidateId);
      if (!el) {
        console.log('🎯 SCROLL_FAILED: Element not found with id:', candidateId);
        // Try to find any product card that contains this token
        const allCards = document.querySelectorAll('.product-card-wrapper');
        console.log('🎯 Available product-card-wrapper IDs:', Array.from(allCards).map(c => c.id).slice(0, 10));
        return false;
      }
      console.log('🎯 SCROLL_SUCCESS: Found and scrolling to', candidateId);
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      // Apply highlight on the target card (not the clicked text)
      window.requestAnimationFrame(() => {
        try {
          el.classList.add('ring-2', 'ring-offset-2', 'ring-teal-500', 'shadow-md', 'bg-teal-50/50');
          setTimeout(() => {
            el.classList.remove('ring-2', 'ring-offset-2', 'ring-teal-500', 'shadow-md', 'bg-teal-50/50');
          }, 1400);
        } catch {}
      });
      return true;
    };

    // First attempt with current DOM
    if (attemptScroll()) return;

    // If target not present (e.g., hidden behind "Show more"), expand all show-more buttons and retry
    const showMoreButtons = Array.from(document.querySelectorAll('[data-product-show-more="true"]')) as HTMLButtonElement[];
    showMoreButtons.forEach(btn => {
      try {
        btn.click();
      } catch {}
    });

    // Retry after layout updates
    setTimeout(() => {
      attemptScroll();
    }, 200);
  }, [productScrollIdByToken]);

  // Linkify product names/SKUs in plain text segments
  const linkifyProductTerms = React.useCallback((text: string): React.ReactNode[] | string => {
    if (!isProductAgentId || !productTokensRegex) {
      console.log('🔗 LINKIFY_SKIP:', { isProductAgentId, hasRegex: !!productTokensRegex, textPreview: text.substring(0, 50) });
      return text;
    }
    const nodes: React.ReactNode[] = [];
    let lastIndex = 0;
    let m: RegExpExecArray | null;
    const regex = new RegExp(productTokensRegex.source, productTokensRegex.flags);
    while ((m = regex.exec(text)) !== null) {
      if (m.index > lastIndex) nodes.push(text.slice(lastIndex, m.index));
      const matched = m[0];
      const normalized = matched.toUpperCase().replace(/[^A-Z0-9]/g, '');
      const anchor = styleCodeToAnchor.get(normalized);
      const isActive = anchor ? (selectedStyleCodesByAnchor[anchor] || []).includes(normalized) : false;
      nodes.push(
        <button
          type="button"
          key={`pl-${m.index}-${matched}`}
          className={`font-semibold cursor-pointer bg-transparent p-0 border-0 focus:outline-none ${
            isActive ? 'text-teal-800' : 'text-teal-700 hover:text-teal-800'
          }`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleStyleCode(matched);
            scrollToProduct(matched);
            try { (e.currentTarget as HTMLButtonElement).blur(); } catch {}
          }}
          style={isActive ? { backgroundColor: 'rgba(13,148,136,0.12)', borderRadius: '6px', padding: '0 2px' } : undefined}
        >
          {matched}
        </button>
      );
      lastIndex = m.index + matched.length;
    }
    if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
    if (nodes.length > 0) {
      console.log('🔗 LINKIFY_SUCCESS: Created', nodes.filter(n => typeof n !== 'string').length, 'clickable links');
    }
    return nodes.length > 0 ? nodes : text;
  }, [isProductAgentId, productTokensRegex, scrollToProduct, selectedStyleCodesByAnchor, styleCodeToAnchor, toggleStyleCode]);


  // Handle message copy (removed from UI but kept for potential future use)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleCopy = () => {
    onCopyMessage(message.content);
  };

  // Handle regenerate (removed from UI but kept for potential future use)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleRegenerate = () => {
    onRegenerateClick(message.id);
  };

  const getFeedbackContext = () => {
    // Find the PRECEDING user message for this agent message
    const messageIndex = messages.findIndex(m => m.id === message.id);
    let userMessage = '';
    
    // Search backwards from the current agent message to find the last user message
    for (let i = messageIndex - 1; i >= 0; i--) {
      if (messages[i].sender === 'user') {
        userMessage = messages[i].content;
        break;
      }
    }
    
    // Get existing feedback if any
    const existingFeedback = (message as any).feedback;
    const existingTags = existingFeedback?.feedbackTags;
    const existingNotes = existingFeedback?.feedbackNotes;
    
    return { userMessage, existingTags, existingNotes };
  };

  // Handle feedback submission - now single click
  const handleFeedback = async (feedback: 'positive' | 'negative') => {
    const { userMessage, existingTags, existingNotes } = getFeedbackContext();
    
    if (feedback === 'positive') {
      // Thumbs up: Submit immediately on click
      await onFeedbackSubmit(message.id, feedback, agent.name, userMessage, message.content);
    } else {
      // Thumbs down: Open the feedback modal by default
      (onFeedbackSubmit as any).openModal?.({
        messageId: message.id,
        feedback: 'negative',
        agentName: agent.name,
        userMessage,
        agentMessage: message.content,
        notesOnly: false,
        existingTags,
        existingNotes,
        allowFeedbackSelection: true,
        allowUpdate: false
      });
    }
  };

  // Handle comment modal - optional deep feedback
  const handleComment = () => {
    const { userMessage, existingTags, existingNotes } = getFeedbackContext();
    const existingFeedbackType = messageFeedback?.positive
      ? 'positive'
      : messageFeedback?.negative
        ? 'negative'
        : null;
    const feedback = existingFeedbackType ?? 'positive';
    const allowFeedbackSelection = true;
    const notesOnly = false;

    (onFeedbackSubmit as any).openModal?.({
      messageId: message.id,
      feedback,
      agentName: agent.name,
      userMessage,
      agentMessage: message.content,
      notesOnly,
      existingTags,
      existingNotes,
      allowFeedbackSelection,
      allowUpdate: !!existingFeedbackType
    });
  };

  // Format timestamp
  const formatTime = (timestamp: Date) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Check if message has citations
  const hasCitations = message.citations && message.citations.length > 0;
  // Check if message has A2A routing
  const hasA2ARouting = message.a2aRouting && Object.keys(message.a2aRouting).length > 0;

  // Helper function to remove Sources section from message content
  const removeSourcesSection = (content: string): string => {
    // Split content into lines for better processing
    const lines = content.split('\n');
    const filteredLines: string[] = [];
    let inSourcesSection = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();
      
      // Check if this line starts a "Sources:" section
      // Handle various formats: "Sources:", "Sources", "**Sources:**", "Sources: ", etc.
      // More comprehensive pattern to catch edge cases
      const sourcesPattern = /^(?:\*\*)?\s*(?:Sources?|sources?):?\s*(?:\*\*)?\s*$/i;
      const isSourcesLine = trimmedLine.match(sourcesPattern) || 
                           (trimmedLine.toLowerCase().includes('sources') && 
                           (trimmedLine.includes(':') || trimmedLine.endsWith('s')));
      
      if (isSourcesLine) {
        inSourcesSection = true;
        continue; // Skip this line
      }
      
      // If we're in a sources section, check if this line is a source entry
      if (inSourcesSection) {
        // If this line is a source entry (starts with [number]), skip it
        if (trimmedLine.match(/^\s*\[\d+\]/)) {
          continue;
        }
        // If this line is empty and the previous line was a source entry, skip it
        if (trimmedLine === '' && i > 0 && lines[i - 1].trim().match(/^\s*\[\d+\]/)) {
          continue;
        }
        // If we encounter a non-source line, we're out of the sources section
        inSourcesSection = false;
      }
      
      // Add the line if we're not in a sources section
      if (!inSourcesSection) {
        filteredLines.push(line);
      }
    }
    
    return filteredLines.join('\n').trim();
  };

  // Process message content - professional markdown rendering
  const processMessageContent = (content: string, citations?: Citation[], onCitationClick?: (citation: Citation) => void) => {
    // Enforce once-per-message image rendering on completed messages too
    // (mirrors StreamingText). See agent-image-citation-contract.md §4.
    const cleanedContent = dedupeImageMarkdown(removeSourcesSection(content));
    
    // DEBUG: Check Top Picks section
    if (cleanedContent.includes('### Top Picks')) {
      const topPicksStart = cleanedContent.indexOf('### Top Picks');
      const topPicksSection = cleanedContent.substring(topPicksStart, Math.min(topPicksStart + 500, cleanedContent.length));
      console.log('🔍 TOP PICKS RAW:', topPicksSection);
      console.log('🔍 TOP PICKS ESCAPED:', JSON.stringify(topPicksSection));
      console.log('🔍 DOUBLE NEWLINES COUNT:', (topPicksSection.match(/\n\n/g) || []).length);
      console.log('🔍 SINGLE NEWLINES COUNT:', (topPicksSection.match(/\n/g) || []).length);
    }
    
    // Build citation map for quick lookup
    const citationMap = new Map<number, Citation>();
    if (citations && citations.length > 0) {
      citations.forEach(citation => {
        if (citation.citation_id) {
          citationMap.set(citation.citation_id, citation);
        }
      });
      
      console.log('📚 Citation map built:', {
        totalCitations: citations.length,
        citationIds: Array.from(citationMap.keys()),
        hasClickHandler: !!onCitationClick
      });
    }
    
    // Helper to process text nodes and replace citation markers
    const processCitationsInText = (text: string): React.ReactNode[] => {
      const parts: React.ReactNode[] = [];
      const citationRegex = /\[(\d+)\]/g;
      let lastIndex = 0;
      let match;
      let hasMatches = false;
      
      while ((match = citationRegex.exec(text)) !== null) {
        hasMatches = true;
        // Add text before citation
        if (match.index > lastIndex) {
          const segment = text.slice(lastIndex, match.index);
          const linked = linkifyProductTerms(segment);
          if (Array.isArray(linked)) {
            parts.push(...linked);
          } else {
            parts.push(linked);
          }
        }
        
        // Add citation link with proper superscript styling
        const citationNumber = parseInt(match[1]);
        const citation = citationMap.get(citationNumber);
        const colorIndex = ((citationNumber - 1) % 5) + 1;
        
        if (citation && onCitationClick) {
          parts.push(
            <span
              key={`cit-${match.index}-${citationNumber}`}
              className="citation-number inline-citation"
              style={{
                color: `var(--citation-color-${colorIndex})`,
                cursor: 'pointer',
                fontSize: '0.7em',
                verticalAlign: 'super',
                margin: '0 2px',
                fontWeight: '700',
                lineHeight: '1'
              }}
              onClick={() => {
                console.log('🖱️ Citation clicked:', {
                  id: citation.citation_id,
                  filename: citation.filename,
                  page: citation.page_number
                });
                onCitationClick(citation);
              }}
              title={`${citation.filename} - Page ${citation.page_number}`}
            >
              [{citationNumber}]
            </span>
          );
        } else {
          // Anti-hallucination guard: a [n] marker that has no backing citation in
          // the emitted (index-derived) citation set is a fabricated reference.
          // Strip it entirely so the user never sees a citation that isn't grounded
          // in real retrieved content. Also absorb a single space immediately
          // preceding the marker to avoid leaving a double-space/orphan gap.
          if (parts.length > 0) {
            const prev = parts[parts.length - 1];
            if (typeof prev === 'string' && prev.endsWith(' ')) {
              parts[parts.length - 1] = prev.replace(/ +$/, '');
            }
          }
          // (push nothing — the marker is removed from the rendered output)
        }
        
        lastIndex = match.index + match[0].length;
      }
      
      // Add remaining text
      if (lastIndex < text.length) {
        const segment = text.slice(lastIndex);
        const linked = linkifyProductTerms(segment);
        if (Array.isArray(linked)) {
          parts.push(...linked);
        } else {
          parts.push(linked);
        }
      }

      if (!hasMatches) {
        const linked = linkifyProductTerms(text);
        return Array.isArray(linked) ? linked : [linked];
      }

      return parts;
    };

    // Compose UI-emoji replacement with citation/product-term processing so the
    // welcome message (and any other agent text) renders Lucide icons in place
    // of warning/calendar/tip glyphs.
    const processTextNode = (text: string): React.ReactNode[] => {
      const emojiParts = replaceUiEmojis(text);
      return emojiParts.flatMap((part) =>
        typeof part === 'string' ? processCitationsInText(part) : [part]
      );
    };

    // Professional markdown components with citation link processing
    const components = {
      h1: ({node, ...props}: any) => <h1 className="text-2xl font-bold mt-4 mb-2" {...props} />,
      h2: ({node, ...props}: any) => <h2 className="text-xl font-bold mt-3 mb-2" {...props} />,
      h3: ({node, ...props}: any) => <h3 className="text-lg font-semibold mt-2 mb-1" {...props} />,
      h4: ({node, ...props}: any) => <h4 className="text-base font-semibold mt-2 mb-1" {...props} />,
      // Process text nodes directly to handle citation markers
      p: ({node, children, ...props}: any) => {
        const processedChildren = React.Children.map(children, child => {
          if (typeof child === 'string') {
            return processTextNode(child);
          }
          return child;
        });
        return <p className="my-4 leading-relaxed" {...props}>{processedChildren}</p>;
      },
      ul: ({node, ...props}: any) => <ul className="list-disc pl-6 my-2 space-y-1" {...props} />,
      ol: ({node, ...props}: any) => <ol className="list-decimal pl-6 my-2 space-y-1" {...props} />,
      // Also handle citations in list items
      li: ({node, children, ...props}: any) => {
        const processedChildren = React.Children.map(children, child => {
          if (typeof child === 'string') {
            return processTextNode(child);
          }
          return child;
        });
        return <li className="leading-relaxed" {...props}>{processedChildren}</li>;
      },
      strong: ({node, children, ...props}: any) => {
        const processedChildren = React.Children.toArray(children).flatMap(child => {
          if (typeof child === 'string') {
            const withCitations = processTextNode(child);
            return (Array.isArray(withCitations) ? withCitations : [withCitations]).flatMap(part => {
              if (typeof part === 'string') {
                const linked = linkifyProductTerms(part);
                return Array.isArray(linked) ? linked : [linked];
              }
              return [part];
            });
          }
          return [child];
        });
        return <strong className="font-semibold" {...props}>{processedChildren}</strong>;
      },
      em: ({node, ...props}: any) => <em className="italic" {...props} />,
      code: ({node, inline, ...props}: any) => 
        inline 
          ? <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-sm" {...props} />
          : <code className="block bg-gray-100 dark:bg-gray-800 p-2 rounded my-2" {...props} />,
      blockquote: ({node, ...props}: any) => 
        <blockquote className="border-l-4 border-gray-300 pl-4 my-2 italic" {...props} />,
      // Hide checkbox inputs from GFM task lists (they appear as empty gray squares)
      input: () => null,
      // Make all links open in new tab
      a: ({node, href, children, ...props}: any) => (
        <a 
          href={href} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-teal-600 dark:text-teal-400 hover:underline"
          {...props}
        >
          {children}
        </a>
      ),
      img: ({node, src, alt}: any) => {
        // Handle broken/inaccessible images
        if (!src || src.includes('[Image not accessible:')) {
          return null; // Don't render broken images
        }

        // Filter out placeholder images (video clapperboards, etc.)
        const srcLower = src.toLowerCase();
        if (srcLower.includes('clapper') || srcLower.includes('video_placeholder') || srcLower.includes('microlearning')) {
          return null; // Don't render placeholder images
        }

        // Convert blob/SharePoint URLs to use image proxy for proper authentication
        let proxiedSrc = src;
        
        // Ensure URL has https:// protocol (Azure Search sometimes returns URLs without protocol)
        if (src && !src.startsWith('http://') && !src.startsWith('https://') && !src.startsWith('data:')) {
          proxiedSrc = `https://${src}`;
        }
        
        // Already proxied by the agent backend? Keep as-is
        const isAlreadyProxied = proxiedSrc.includes('/api/image-proxy');
        
        if (!isAlreadyProxied) {
          if (proxiedSrc.includes('blob.core.windows.net')) {
            // Blob URL → always proxy. A SAS token isn't enough when the storage
            // account is behind a network firewall (e.g. prod document-images),
            // so the server-side proxy fetches it with the account key instead.
            const encodedUrl = encodeURIComponent(proxiedSrc);
            proxiedSrc = `${process.env.REACT_APP_API_URL || ''}/api/image-proxy?imageUrl=${encodedUrl}`;
          } else if (proxiedSrc.includes('.sharepoint.com/')) {
            // SharePoint URL → proxy via Express (Graph API sharing links)
            const encodedUrl = encodeURIComponent(proxiedSrc);
            proxiedSrc = `${process.env.REACT_APP_API_URL || ''}/api/image-proxy?imageUrl=${encodedUrl}`;
          }
        }

        // Check if this is a product image (from agent-product-images container)
        // Only product catalog images should render normally, not with ScaledImage
        // Documentation images (owensboro2, unifirst_docs, etc.) should use ScaledImage
        const isProductImage = proxiedSrc.includes('agent-product-images');
        
        // Hide all images if imageMaxWidth is 'hidden'
        if (imageMaxWidth === 'hidden') {
          return null;
        }
        
        if (isProductImage) {
          // Render product catalog images normally (for product cards)
          return (
            <img
              src={proxiedSrc}
              alt={alt || 'Product image'}
              className="max-w-full h-auto rounded-lg"
              loading="lazy"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
                console.warn('Failed to load product image:', src);
              }}
            />
          );
        }

        // Use ScaledImage component for documentation images only
        return <ScaledImage src={proxiedSrc} alt={alt || 'Image'} maxWidth={imageMaxWidth} />;
      },
    };

    return (
      <div className="markdown-content">
        <ReactMarkdown 
          remarkPlugins={[remarkGfm]}
          components={components}
        >
          {cleanedContent}
        </ReactMarkdown>
      </div>
    );
  };

  // Helper function to process text and replace citations
  const processTextWithCitations = (text: string, citationMap: Map<number, Citation>, onCitationClick?: (citation: Citation) => void) => {
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    const citationRegex = /\[(\d+)\]/g;
    let match;
    
    // Enhanced debug: Log text processing with more context
    console.log('🔤 Processing text for citations:', {
      agentName: message.agentName || 'unknown',
      textLength: text.length,
      mapSize: citationMap.size,
      mapKeys: Array.from(citationMap.keys()),
      textPreview: text.substring(0, 200),
      hasCitationBrackets: /\[\d+\]/.test(text),
      regexPattern: citationRegex.toString()
    });

    while ((match = citationRegex.exec(text)) !== null) {
      const citationNumber = parseInt(match[1]);
      const citation = citationMap.get(citationNumber);
      
      // Enhanced debug: Log each citation match with more details
      console.log(`🔢 Citation match [${citationNumber}] at index ${match.index}:`, {
        found: !!citation,
        matchedText: match[0],
        citationData: citation ? {
          id: citation.citation_id,
          filename: citation.filename?.substring(0, 30),
          page: citation.page_number
        } : 'not in map',
        mapKeys: Array.from(citationMap.keys())
      });
      if (citation) {
        // Add text before the citation
        if (match.index > lastIndex) {
          const segment = text.slice(lastIndex, match.index);
          const linked = linkifyProductTerms(segment);
          if (Array.isArray(linked)) parts.push(...linked); else parts.push(linked);
        }

        // Add the styled citation
        const colorIndex = ((citationNumber - 1) % 5) + 1;
        parts.push(
          <span
            key={`citation-${citationNumber}-${match.index}`}
            className="citation-number inline-citation"
            style={{
              color: `var(--citation-color-${colorIndex})`,
              cursor: 'pointer',
              fontSize: '0.7em',
              verticalAlign: 'super',
              margin: '0 2px',
              fontWeight: '700',
              lineHeight: '1'
            }}
            onClick={() => {
              console.log('🖱️ Citation clicked:', {
                id: citation.citation_id,
                filename: citation.filename,
                page: citation.page_number
              });
              onCitationClick && onCitationClick(citation);
            }}
          >
            [{citationNumber}]
          </span>
        );

        lastIndex = match.index + match[0].length;
      } else {
        // Anti-hallucination guard: drop [n] markers with no backing citation so a
        // fabricated reference is never rendered (only index-grounded citations show).
        if (match.index > lastIndex) {
          const segment = text.slice(lastIndex, match.index).replace(/ +$/, '');
          const linked = linkifyProductTerms(segment);
          if (Array.isArray(linked)) parts.push(...linked); else parts.push(linked);
        }
        lastIndex = match.index + match[0].length;
      }
    }

    // Add remaining text
    if (lastIndex < text.length) {
      const segment = text.slice(lastIndex);
      const linked = linkifyProductTerms(segment);
      if (Array.isArray(linked)) parts.push(...linked); else parts.push(linked);
    }

    return parts.length > 1 ? parts : text;
  };

     // Get conversation starters from SAMPLE_QUESTIONS (with categories) or fallback to agents.json
   const conversationStarters: SampleQuestion[] = useMemo(() => {
     const agentQuestions = SAMPLE_QUESTIONS[agent.id];
     if (agentQuestions && agentQuestions.length > 0) {
       return agentQuestions;
     }
     // Fallback to agents.json sampleQuestions (convert to SampleQuestion format)
     return (agent.sampleQuestions || []).map((q: string, i: number) => ({
       id: `${agent.id}-fallback-${i}`,
       question: q,
       category: 'General',
       agent: agent.id as SampleQuestion['agent'],
       iconName: 'HelpCircle' as SampleQuestion['iconName']
     }));
   }, [agent.id, agent.sampleQuestions]);

   // Helper function to extract citation numbers referenced in the message content
  const getReferencedCitationNumbers = (content: string): Set<number> => {
    const citationNumbers = new Set<number>();
    const citationRegex = /\[(\d+)\]/g;
    let match;
    
    while ((match = citationRegex.exec(content)) !== null) {
      citationNumbers.add(parseInt(match[1]));
    }
    
    return citationNumbers;
  };

  const metadataSections = (() => {
    const sections: React.ReactNode[] = [];

    if (message.routingMetadata) {
      const routing = message.routingMetadata;
      sections.push(
        <details key="routing" className="group border border-gray-100 rounded-lg px-3 py-2 bg-gray-50/50 open:bg-white">
          <summary className="text-xs font-semibold text-gray-600 cursor-pointer flex items-center justify-between">
            <span>Routing Info</span>
            <span className="text-gray-400 transition-transform group-open:rotate-90">▸</span>
          </summary>
          <div className="mt-2 text-xs text-gray-700 space-y-2">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-800">
                {routing.selected_agent_name || routing.selected_agent_id || 'Unknown Agent'}
              </span>
              {routing.confidence !== undefined && (
                <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                  routing.confidence >= 0.9 ? 'bg-brand-50 text-brand-700 border border-brand-200' :
                  routing.confidence >= 0.75 ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                  routing.confidence >= 0.5 ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' :
                  'bg-gray-50 text-gray-700 border border-gray-200'
                }`}>
                  {(routing.confidence * 100).toFixed(0)}% confidence
                </span>
              )}
              {routing.forced && (
                <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-orange-50 text-orange-600 border border-orange-200">
                  Forced
                </span>
              )}
            </div>
            {routing.rationale && (
              <p className="text-gray-500">{routing.rationale}</p>
            )}
            {routing.all_scores && Object.keys(routing.all_scores).length > 1 && (
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">Confidence Scores</p>
                {Object.entries(routing.all_scores)
                  .sort(([, a], [, b]) => (b as number) - (a as number))
                  .map(([agentId, score]) => (
                    <div key={agentId} className="flex items-center justify-between gap-2 px-2 py-1 rounded bg-gray-50">
                      <span className="font-medium text-gray-700">{agentId}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${
                              (score as number) >= 0.9 ? 'bg-brand-500' :
                              (score as number) >= 0.75 ? 'bg-blue-500' :
                              (score as number) >= 0.5 ? 'bg-yellow-500' :
                              'bg-gray-400'
                            }`}
                            style={{ width: `${(score as number) * 100}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-600 w-10 text-right">
                          {((score as number) * 100).toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            )}
            {routing.candidates && routing.candidates.length > 0 && (
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">Candidates</p>
                {routing.candidates.map(candidate => (
                  <div key={`${candidate.agent_id}-${candidate.status}`} className="flex items-start justify-between gap-2 border border-gray-100 rounded-lg px-2 py-1.5 bg-white">
                    <div>
                      <div className="font-semibold text-gray-800">
                        {candidate.agent_name || candidate.agent_id}
                        <span className="ml-2 text-[10px] uppercase tracking-wide text-gray-400">{candidate.status}</span>
                      </div>
                      {candidate.reason && (
                        <p className="text-gray-500">{candidate.reason}</p>
                      )}
                    </div>
                    {typeof candidate.score === 'number' && (
                      <span className="text-xs text-gray-500">
                        {(candidate.score * 100).toFixed(0)}%
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
            {routing.consulted_agents && routing.consulted_agents.length > 0 && (
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">Consulted Agents</p>
                {routing.consulted_agents.map((agent: any, idx: number) => {
                  const status = agent.status || (agent.success ? 'success' : 'failed');
                  const statusStyles: Record<string, { bg: string; text: string; label: string }> = {
                    success: { bg: 'bg-brand-100', text: 'text-brand-700', label: '✓' },
                    timed_out: { bg: 'bg-amber-100', text: 'text-amber-700', label: '⏱ timeout' },
                    failed: { bg: 'bg-red-100', text: 'text-red-700', label: '✗ failed' },
                    error: { bg: 'bg-red-100', text: 'text-red-700', label: '⚠ error' },
                  };
                  const style = statusStyles[status] || statusStyles.failed;
                  
                  return (
                    <div key={`consulted-${agent.agent_id}-${idx}`} className="flex items-start justify-between gap-2 border border-blue-100 rounded-lg px-2 py-1.5 bg-blue-50/50">
                      <div>
                        <div className="font-semibold text-blue-800 flex items-center gap-1.5">
                          <span className="text-blue-500">🔗</span>
                          {agent.agent_name || agent.agent_id}
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${style.bg} ${style.text}`}>
                            {style.label}
                          </span>
                        </div>
                        {agent.reason && (
                          <p className="text-blue-600 text-[11px]">{agent.reason}</p>
                        )}
                        {agent.keywords && agent.keywords.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {agent.keywords.slice(0, 5).map((kw: string, kwIdx: number) => (
                              <span key={kwIdx} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-600">
                                {kw}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </details>
      );
    }

    if (message.search_summary && ((message.search_summary.events && message.search_summary.events.length > 0) || (message.search_summary.sections && message.search_summary.sections.length > 0))) {
      sections.push(
        <details key="tools" className="group border border-gray-100 rounded-lg px-3 py-2 bg-gray-50/50 open:bg-white">
          <summary className="text-xs font-semibold text-gray-600 cursor-pointer flex items-center justify-between">
            <span>Tool Details</span>
            <span className="text-gray-400 transition-transform group-open:rotate-90">▸</span>
          </summary>
          <div className="mt-2 space-y-3 text-xs text-gray-700">
            {message.search_summary.sections ? (
              message.search_summary.sections.map((section: any, sectionIndex: number) => (
                <details key={sectionIndex} className="space-y-2 group" open={!message.search_summary?.default_section_collapsed}>
                  <summary className="text-xs font-semibold text-gray-600 uppercase tracking-wide cursor-pointer hover:text-gray-800 flex items-center gap-1 list-none">
                    <span className="inline-block w-3 text-center transition-transform group-open:rotate-90">▶</span>
                    {section.section_name}
                  </summary>
                  <div className="ml-4 mt-2 space-y-2">
                    {section.events.map((event: any, eventIndex: number) => {
                      const impactColors = {
                        positive: 'text-green-700 bg-green-50 border-green-200',
                        neutral: 'text-blue-700 bg-blue-50 border-blue-200',
                        negative: 'text-red-700 bg-red-50 border-red-200',
                        info: 'text-gray-700 bg-gray-50 border-gray-200',
                      };

                      const IconComponent = event.icon ? getSearchEventIcon(event.icon) : null;
                      const impactKey = (event.impact || 'info') as 'positive' | 'neutral' | 'negative' | 'info';

                      return (
                        <div
                          key={eventIndex}
                          className={`text-xs p-2 rounded border ${impactColors[impactKey]}`}
                        >
                          <div className="font-semibold mb-1 flex items-center gap-1.5">
                            {IconComponent && <IconComponent className="w-3.5 h-3.5 flex-shrink-0" />}
                            <span className="flex-1">{event.title}</span>
                          </div>
                          <div className="text-gray-600">{event.description}</div>
                          {event.collapsible && event.details && event.details.length > 0 && (
                            <details className="mt-2">
                              <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700">
                                View details
                              </summary>
                              <div className="mt-2 pl-3 border-l-2 border-gray-300 space-y-1">
                                {event.details.map((detail: string, detailIndex: number) => (
                                  <div key={detailIndex} className="text-gray-500 text-xs">
                                    {detail}
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </details>
              ))
            ) : (
              message.search_summary.events && message.search_summary.events.map((event, index) => {
                const impactColors = {
                  positive: 'text-green-700 bg-green-50 border-green-200',
                  neutral: 'text-blue-700 bg-blue-50 border-blue-200',
                  negative: 'text-red-700 bg-red-50 border-red-200',
                  info: 'text-gray-700 bg-gray-50 border-gray-200',
                };
                const IconComponent = event.icon ? getSearchEventIcon(event.icon) : null;
                return (
                  <div
                    key={index}
                    className={`text-xs p-2 rounded border ${impactColors[event.impact]}`}
                  >
                    <div className="font-semibold mb-1 flex items-center gap-1.5">
                      {IconComponent && <IconComponent className="w-3.5 h-3.5 flex-shrink-0" />}
                      <span>{event.title}</span>
                    </div>
                    <div className="text-gray-600">{event.description}</div>
                  </div>
                );
              })
            )}
          </div>
        </details>
      );
    }

    if (hasCitations && message.citations) {
      sections.push(
        <details key="sources" className="group border border-gray-100 rounded-lg px-3 py-2 bg-gray-50/50 open:bg-white">
          <summary className="text-xs font-semibold text-gray-600 cursor-pointer flex items-center justify-between">
            <span>Sources</span>
            <span className="text-gray-400 transition-transform group-open:rotate-90">▸</span>
          </summary>
          <div className="mt-2 space-y-1 text-xs text-gray-700">
            {(() => {
              const referencedNumbers = getReferencedCitationNumbers(message.content);
              const referencedCitations = (message.citations || []).filter((citation) => 
                referencedNumbers.has(citation.citation_id)
              );
              const citationsToShow = referencedCitations.length > 0
                ? referencedCitations
                : (message.citations || []);
              
              return citationsToShow.map((citation, index) => {
                const colorIndex = (index % 5) + 1;
                const fallbackColors = ['#00b194', '#0066cc', '#d63384', '#fd7e14', '#6f42c1'];
                const citationColor = `var(--citation-color-${colorIndex}, ${fallbackColors[colorIndex - 1]})`;
                const citationNumber = citation.citation_id;
                // Display section for UniFirst Docs, page number for other agents
                const section = citation.section || citation.metadata?.section_h2 || '';
                const sectionInfo = section 
                  ? section 
                  : (citation.page_number ? `Page ${citation.page_number}` : '');
                return (
                  <div key={index} className="text-xs">
                    <span
                      className="citation-number"
                      style={{ color: citationColor, fontWeight: 'bold', cursor: 'pointer' }}
                      onClick={() => {
                        onCitationClick && onCitationClick(citation);
                      }}
                    >
                      [{citationNumber}]
                    </span>
                    <span className="text-gray-600 ml-1">{citation.filename}</span>
                    {sectionInfo && <span className="text-gray-400 ml-1">{sectionInfo}</span>}
                  </div>
                );
              });
            })()}
          </div>
        </details>
      );
    }

    return sections;
  })();
  const hasMetadataSections = metadataSections.length > 0;
  const isMetadataOpen = hasMetadataSections ? isSourcesSectionExpanded : false;
  // Only show timestamp when message is truly complete (not streaming, not status, and isCompleted is true)
  // For user messages, isCompleted may be undefined, so we check if it's explicitly false
  const isMessageComplete = !isCurrentlyStreaming && !message.isStatusMessage && message.isCompleted !== false;
  const timestampNode = isMessageComplete ? (
    <span className="text-xs text-gray-500 whitespace-nowrap">{formatTime(message.timestamp)}</span>
  ) : null;
  const isAnimatingWithoutFeedback = thumbsAnimating && !messageFeedback?.positive && !messageFeedback?.negative;
  const feedbackControls = (isAgentMessage && isMessageComplete) ? (
    <div className="flex items-center gap-1">
      <button 
        className={`p-1 transition-colors ${
          messageFeedback?.positive 
            ? 'text-teal-600' 
            : isAnimatingWithoutFeedback 
              ? `${glowingThumb === 'up' ? 'text-green-500' : 'text-gray-400'} thumbs-wiggle-up` 
              : 'text-gray-400 hover:text-teal-600'
        }`}
        onClick={() => handleFeedback('positive')}
        title="This response was helpful"
        type="button"
      >
        <ThumbsUp size={20} />
      </button>
      <button 
        className={`p-1 transition-colors ${
          messageFeedback?.negative 
            ? 'text-teal-600' 
            : isAnimatingWithoutFeedback 
              ? `${glowingThumb === 'down' ? 'text-red-500' : 'text-gray-400'} thumbs-wiggle-down` 
              : 'text-gray-400 hover:text-teal-600'
        }`}
        onClick={() => handleFeedback('negative')}
        title="This response was not helpful"
        type="button"
      >
        <ThumbsDown size={20} />
      </button>
      <button
        className="p-1 transition-colors text-gray-400 hover:text-teal-600"
        onClick={handleComment}
        title="Add a comment"
        type="button"
      >
        <MessageSquare size={18} />
      </button>
    </div>
  ) : null;
  const metadataToggleButton = hasMetadataSections ? (
    <button
      type="button"
      onClick={onToggleSourcesSection}
      className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-gray-800 transition-colors"
      aria-expanded={isMetadataOpen}
    >
      <Info size={14} className="text-teal-500" />
      <span>More Info</span>
      <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isMetadataOpen ? 'rotate-180' : ''}`} />
    </button>
  ) : null;



  if (isSystemMessage) {
    return null;
  }

  return (
    <div className={`flex ${isUserMessage ? 'justify-end' : 'justify-start'} mb-4 px-0`}>
      <div className={`flex items-start gap-2 sm:gap-3 min-w-0 ${isUserMessage ? 'flex-row-reverse' : 'flex-row'}`} 
           style={{ maxWidth: 'calc(100% - 10vw)' }}>
                 {/* Agent Avatar */}
         {isAgentMessage && (
           <div className="flex-shrink-0">
             <div className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden">
               <AgentIcon 
                 icon={agent.icon} 
                 alt={agent.name}
                 size="medium"
               />
             </div>
           </div>
         )}
        
        {/* Message Bubble */}
        <div className={`flex flex-col ${isUserMessage ? 'items-end' : 'items-start'}`}>
          {/* Message Header - removed per user request */}
          
                    {/* Message Content */}
          <div className={`rounded-2xl px-3 sm:px-4 py-2 sm:py-3 shadow-md break-words min-w-0 ${
            isUserMessage 
              ? 'bg-teal-500 text-white rounded-br-md' 
              : message.isStatusMessage
                ? 'bg-transparent border-none shadow-none italic text-gray-500 dark:text-gray-400 status-message-pulse'
                : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-bl-md border border-gray-200 dark:border-gray-700'
          }`}>
            {isWelcomeMessage && showConversationStarters ? (
              <div className="space-y-4">
                <div className="markdown-content break-words" style={{ maxWidth: '80vw' }}>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={buildEmojiAwareMarkdownComponents()}
                  >
                    {message.content}
                  </ReactMarkdown>
                </div>
                {/* If this is a returning-user short welcome, expose the full
                    introduction behind a "Show full welcome message" toggle.
                    Only render after the mock-stream finishes so the toggle
                    doesn't pop in mid-animation. */}
                {message.isCompleted && typeof message.metadata?.fullWelcomeMessage === 'string' && (
                  <div className="animate-fade-in">
                    <button
                      type="button"
                      onClick={() => setIsFullWelcomeExpanded((open) => !open)}
                      className="flex items-center gap-1.5 text-xs font-medium text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 transition-colors"
                      aria-expanded={isFullWelcomeExpanded}
                    >
                      <ChevronDown
                        className={`w-3.5 h-3.5 transition-transform ${isFullWelcomeExpanded ? 'rotate-180' : ''}`}
                      />
                      <span>{isFullWelcomeExpanded ? 'Hide full welcome message' : 'Show full welcome message'}</span>
                    </button>
                    {isFullWelcomeExpanded && (
                      <div className="markdown-content break-words mt-3 pt-3 border-t border-gray-200 dark:border-gray-700" style={{ maxWidth: '80vw' }}>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={buildEmojiAwareMarkdownComponents()}
                        >
                          {message.metadata.fullWelcomeMessage as string}
                        </ReactMarkdown>
                      </div>
                    )}
                  </div>
                )}
                {/* Persistent project-contact line — kept OUTSIDE the
                    "Show full welcome message" dropdown so the email stays
                    visible for returning users even when the full intro is
                    collapsed. */}
                {message.isCompleted && agent.projectContactEmail && (
                  <div className="text-sm text-gray-600 dark:text-gray-300 animate-fade-in">
                    Questions? Email the project team at{' '}
                    <a
                      href={`mailto:${agent.projectContactEmail}`}
                      className="text-teal-600 dark:text-teal-400 hover:underline"
                    >
                      {agent.projectContactEmail}
                    </a>
                    .
                  </div>
                )}
                {/* Agent-admin affordance: edit the "What's New" note shown in
                    this agent's intro. Gated by canEditWhatsNew (super-admin or
                    this agent's admin). Saves to agents.json via the scoped
                    /admin/registry/:agentId/whats-new endpoint. */}
                {message.isCompleted && canEditWhatsNew && onEditWhatsNew && (
                  <div className="animate-fade-in pt-1">
                    <button
                      type="button"
                      onClick={onEditWhatsNew}
                      className="flex items-center gap-1.5 text-xs font-medium text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 transition-colors"
                      title="Edit the What's New note shown in this agent's intro (admins only)"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      <span>Edit What's New</span>
                    </button>
                  </div>
                )}
                {/* Show conversation starters only after mock stream completes.
                    Render the first 3 by default and tuck the rest behind a
                    "Show more sample questions" disclosure. */}
                {message.isCompleted && conversationStarters.length > 0 && (() => {
                  const visibleStarters = conversationStarters.slice(0, VISIBLE_STARTERS_COUNT);
                  const hiddenStarters = conversationStarters.slice(VISIBLE_STARTERS_COUNT);
                  const renderStarter = (starter: SampleQuestion, index: number) => (
                    <button
                      key={starter.id}
                      className="text-left text-sm text-teal-600 hover:text-teal-700 hover:bg-teal-50 dark:text-teal-400 dark:hover:text-teal-300 dark:hover:bg-teal-900/30 p-2 rounded-lg transition-colors break-words"
                      onClick={() => onSuggestionClick(starter.question)}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <span className="inline-block text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded mr-2">
                        {starter.category}
                      </span>
                      <span className="break-words">{starter.question}</span>
                    </button>
                  );
                  return (
                    <div className="border-t pt-4 animate-fade-in">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                        <Lightbulb className="h-4 w-4 inline mr-1" />
                        Try asking:
                      </h4>
                      <div className="grid grid-cols-1 gap-2">
                        {visibleStarters.map(renderStarter)}
                        {areMoreStartersOpen && hiddenStarters.map((s, i) => renderStarter(s, i + VISIBLE_STARTERS_COUNT))}
                      </div>
                      {hiddenStarters.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setAreMoreStartersOpen((open) => !open)}
                          className="mt-3 flex items-center gap-1.5 text-xs font-medium text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 transition-colors"
                          aria-expanded={areMoreStartersOpen}
                        >
                          <ChevronDown
                            className={`w-3.5 h-3.5 transition-transform ${areMoreStartersOpen ? 'rotate-180' : ''}`}
                          />
                          <span>
                            {areMoreStartersOpen
                              ? 'Show fewer sample questions'
                              : `Show ${hiddenStarters.length} more sample question${hiddenStarters.length === 1 ? '' : 's'}`}
                          </span>
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>
            ) : message.isStatusMessage ? (
              <div className="aldo-thinking-dots-wrap" aria-label="Thinking" role="status">
                {isGenericStatus(message.content) ? (
                  <span className="aldo-thinking-dots" aria-hidden="true">
                    <span></span><span></span><span></span>
                  </span>
                ) : (
                  <span className="aldo-thinking-label">{cleanStatusLabel(message.content)}</span>
                )}
              </div>
            ) : (
              <div className="markdown-content" style={{ maxWidth: '80vw' }}>
                {(() => {
                  // Prefer smooth animated reveal during generic streaming (no complex product JSON/anchor rendering)
                  const streamingContent = currentStreamingMessage || message.content;
                  if (isCurrentlyStreaming && !message.isCompleted) {
                    const hasProductAnchors = /\[(?:PRODUCTS|PRODUCT_CARDS):/.test(streamingContent);
                    const hasJsonBlocks = /```json[\s\S]*?```/g.test(streamingContent);
                    if (!hasProductAnchors && !hasJsonBlocks) {
                      return (
                        <StreamingText 
                          content={streamingContent}
                          isComplete={false}
                          className="streaming-message-content break-words"
                          imageMaxWidth={imageMaxWidth}
                        />
                      );
                    }
                  }
                  
                  // We must continue the function here instead of having an early return

                  // Check if this is the product agent 
                  const isProductAgent = agent && agent.id === 'product';
                  
                  // Removed debug logging to avoid re-render storms
                  
                  // Check if this is the product agent (check multiple possible IDs) OR if the message contains product card anchors
                  const isProductAgentId = agent && (
                    agent.id === 'product' || 
                    agent.id === 'product_finder_assistant' || 
                    agent.id.includes('product') ||
                    agent.id.includes('Product')
                  );
                  
                  // Avoid heavy anchor processing unless we actually have productDisplays to match
                  const hasProductDisplaysMap = !!(effectiveProductDisplays && Object.keys(effectiveProductDisplays).length > 0);
                  
                  const hasProductCards = /\[(?:PRODUCTS|PRODUCT_CARDS):/.test(message.content);
                  
                  // Debug logging for product card detection
                  console.log('🔍 Product Card Detection:', {
                    messageId: message.id,
                    messageSender: message.sender,
                    isProductAgentId,
                    hasProductCards,
                    hasProductDisplaysMap,
                    productDisplaysKeys: effectiveProductDisplays ? Object.keys(effectiveProductDisplays) : [],
                    messageContentPreview: message.content.substring(0, 200),
                    fullContentLength: message.content.length,
                    searchForMarker: /\[(?:PRODUCTS|PRODUCT_CARDS):/.test(message.content),
                    markerCount: (message.content.match(/\[(?:PRODUCTS|PRODUCT_CARDS):/g) || []).length
                  });
                  
                  // Allow anchor-based card injection for both completed and currently streaming message
                  if ((isProductAgentId || hasProductCards) && hasProductDisplaysMap) {
                    // Match both [PRODUCTS: N] (new) and [PRODUCT_CARDS: X] (legacy)
                    const anchorRegex = /\[(?:PRODUCTS|PRODUCT_CARDS):\s*([^\]]+)\]/g;
                    let idx = 0;
                    const parts: React.ReactNode[] = [];
                    let last = 0;
                    let m: RegExpExecArray | null;
                    while ((m = anchorRegex.exec(message.content)) !== null) {
                      const pre = message.content.slice(last, m.index);
                      if (pre) {
                        parts.push(
                          <div key={`pre-${idx}`}>{processMessageContent(pre, message.citations, onCitationClick)}</div>
                        );
                      }
                      const anchor = m[1].trim();
                      // Removed debug logging to avoid re-render storms
                      
                      // Removed debug logging to avoid re-render storms
                      // Enhanced anchor matching logic to handle variant SKUs and base SKUs
                      // Clean the anchor to alphanumeric only (remove dashes/whitespace)
                      const cleanAnchor = anchor.replace(/[^a-zA-Z0-9]/g, '');
                      const lowerAnchor = cleanAnchor.toLowerCase();
                      const upperAnchor = cleanAnchor.toUpperCase();
                      
                      // Derive a 4-char base code from the anchor per SKU rules:
                      // - If length >= 6 (numeric or alphanumeric), use first 4 chars
                      // - If 5 numeric, pad to 6 with leading 0, then take first 4
                      // - Otherwise, pad to 4 on the left and take first 4
                      let base4 = upperAnchor;
                      if (/^\d+$/.test(upperAnchor)) {
                        // Pure numeric: use first 4 digits directly
                        base4 = upperAnchor.slice(0, 4);
                      } else if (upperAnchor.length >= 6) {
                        // Alphanumeric long codes: use first 4 chars
                        base4 = upperAnchor.slice(0, 4);
                      } else {
                        // Short codes: left-pad to 4 and take first 4
                        base4 = upperAnchor.padStart(4, '0').slice(0, 4);
                      }
                      
                      // Try to find products for this anchor using various matching strategies
                      let products: any[] = [];
                      let displayHeading = '';
                      let displayFacets: any[] = [];
                      
                      // Helper to extract data from display object (new format) or array (legacy)
                      const extractDisplayData = (displayData: any) => {
                        if (Array.isArray(displayData)) {
                          // Legacy format: just products array
                          return { products: displayData, heading: '', facets: [] };
                        } else if (displayData && typeof displayData === 'object') {
                          // New format: { products, heading, facets }
                          return {
                            products: displayData.products || [],
                            heading: displayData.heading || '',
                            facets: displayData.facets || []
                          };
                        }
                        return { products: [], heading: '', facets: [] };
                      };
                      
                      if (effectiveProductDisplays) {
                        let displayData: any = null;

                        const resolvedKey = resolveProductDisplayKey(
                          anchor,
                          effectiveProductDisplays as Record<string, unknown>,
                          idx
                        );
                        if (resolvedKey) {
                          displayData = effectiveProductDisplays[resolvedKey];
                        }

                        // Legacy SKU-style anchor matching (style codes in anchor text)
                        if (!displayData) {
                          if (effectiveProductDisplays[anchor]) {
                            displayData = effectiveProductDisplays[anchor];
                          } else if (effectiveProductDisplays[lowerAnchor]) {
                            displayData = effectiveProductDisplays[lowerAnchor];
                          } else if (effectiveProductDisplays[upperAnchor]) {
                            displayData = effectiveProductDisplays[upperAnchor];
                          } else if (effectiveProductDisplays[base4]) {
                            displayData = effectiveProductDisplays[base4];
                          } else {
                            const keyByProductBase = Object.keys(effectiveProductDisplays).find(k => {
                              const data = effectiveProductDisplays[k] as any;
                              const arr = Array.isArray(data) ? data : (data?.products || []);
                              return arr.some((p: any) => (p.base_sku || '').toString().toUpperCase() === base4);
                            });
                            if (keyByProductBase) {
                              displayData = effectiveProductDisplays[keyByProductBase];
                            }
                          }
                        }
                        
                        if (displayData) {
                          const extracted = extractDisplayData(displayData);
                          products = extracted.products;
                          displayHeading = extracted.heading;
                          displayFacets = extracted.facets;
                        }

                        // 6. Fuzzy matching - look for any key that contains the anchor or starts with base4
                        if (!products || products.length === 0) {
                          // Get all keys that contain this anchor or base
                          const matchingKeys = Object.keys(effectiveProductDisplays).filter(key => {
                            const data = effectiveProductDisplays[key] as any;
                            const arr = Array.isArray(data) ? data : (data?.products || []);
                            return (
                              key.includes(anchor) || 
                              key.includes(lowerAnchor) || 
                              key.includes(upperAnchor) || 
                              key.startsWith(base4) ||
                              arr.some((p: any) => 
                                (p.color_variants || []).some((v: any) => 
                                  (v.variant_sku || '').toString().toUpperCase().includes(upperAnchor) || 
                                  (v.variant_sku || '').toString().toLowerCase().includes(lowerAnchor)
                                )
                              )
                            );
                          });
                          
                          if (matchingKeys.length > 0) {
                            // Use the first matching key's data
                            const fuzzyData = effectiveProductDisplays[matchingKeys[0]];
                            const extracted = extractDisplayData(fuzzyData);
                            products = extracted.products;
                            displayHeading = extracted.heading;
                            displayFacets = extracted.facets;
                          }
                        }
                      }
                      
                      // Debug logging for product matching - expanded for facets
                      console.log(`📦 Anchor "${anchor}" matched ${products.length} products`, {
                        heading: displayHeading,
                        facetCount: displayFacets.length,
                        facetFieldNames: displayFacets.map((f: any) => f.field_name),
                        facetValueCounts: displayFacets.map((f: any) => ({ field: f.field_name, values: f.values?.length || 0 })),
                        rawFacets: displayFacets,  // Full facet data
                        willPassToProductDisplay: displayFacets.length > 0,
                        products: products.slice(0, 3).map((p: any) => p.name || p.base_sku)
                      });
                      
                      if (products && products.length > 0) {
                        // Successful match - render product display with message-specific card mode
                        // Facets are sent from backend in the product_display event
                        const productDisplayData: ProductDisplayData = {
                          type: 'product_display',
                          products: products,
                          heading: displayHeading || undefined,
                          facets: displayFacets.length > 0 ? displayFacets : undefined,
                          search_info: {
                            total_found: products.length
                          }
                        };
                        
                        // Calculate product card scale from image scale (0 = 0.45 small/condensed, 50 = 0.75, 100 = 1.0 full)
                        const productScale = imageScale <= 0 ? 0.45 : imageScale >= 100 ? 1.0 : 0.45 + (imageScale / 100) * 0.55;
                        
                        // Pass anchor-specific selected codes
                        const anchorSelectedCodes = getSelectedCodesForAnchor(anchor);
                        
                        parts.push(
                          <div key={`cards-${idx}`} className="product-cards-scrollable-section my-6 max-w-full overflow-hidden">
                            <ProductDisplay 
                              data={productDisplayData}
                              ProductCardComponent={ProductCard}
                              displayMode={message.productCardMode}
                              scale={productScale}
                              selectedStyleCodes={anchorSelectedCodes}
                              onSelectedStyleCodesChange={(codes) => setSelectedCodesForAnchor(anchor, codes)}
                            />
                          </div>
                        );
                      } else {
                        // No match found - show nothing if we don't have payload yet; keep placeholder text minimal
                      }
                      last = m.index + m[0].length;
                      idx++;
                    }
                    const tail = message.content.slice(last);
                    if (parts.length > 0) {
                      if (tail) {
                        // CRITICAL: Check if tail contains [ALTERNATIVES: anchor] markers before adding as plain text
                        // This ensures alternatives replacement happens even when product cards are present
                        const msgFilterAlts = (message as any).filterAlternatives;
                        const msgAltDisplays = (message as any).alternativeDisplays;
                        const messageAlternativeDisplays = msgFilterAlts 
                          ? { [msgFilterAlts.anchorId]: msgFilterAlts }
                          : (msgAltDisplays || alternativeDisplays);
                        
                        if (tail.includes('[ALTERNATIVES:') && messageAlternativeDisplays && Object.keys(messageAlternativeDisplays).length > 0) {
                          // Replace alternatives in tail
                          const alternativesRegex = /\[ALTERNATIVES:\s*([^\]]+)\]/g;
                          const tailParts: React.ReactNode[] = [];
                          let tailLast = 0;
                          let tailMatch: RegExpExecArray | null;
                          let tailIdx = 0;
                          
                          while ((tailMatch = alternativesRegex.exec(tail)) !== null) {
                            const preTail = tail.slice(tailLast, tailMatch.index);
                            if (preTail) {
                              tailParts.push(
                                <div key={`tail-pre-${tailIdx}`}>{processMessageContent(preTail, message.citations, onCitationClick)}</div>
                              );
                            }
                            
                            const anchorId = tailMatch[1].trim();
                            const altData = messageAlternativeDisplays[anchorId];
                            
                            if (altData && altData.alternatives && altData.alternatives.length > 0) {
                              tailParts.push(
                                <div key={`tail-alt-${tailIdx}`} className="my-4">
                                  <FilterAlternatives
                                    unmappedFilter={altData.unmappedFilter}
                                    unmappedValue={altData.unmappedValue}
                                    alternatives={altData.alternatives}
                                    onSelect={(filters, description) => {
                                      onSendMessage(`Show me: ${description}`);
                                    }}
                                  />
                                </div>
                              );
                            }
                            
                            tailLast = tailMatch.index + tailMatch[0].length;
                            tailIdx++;
                          }
                          
                          const tailRemainder = tail.slice(tailLast);
                          if (tailRemainder) {
                            tailParts.push(
                              <div key={`tail-remainder`}>{processMessageContent(tailRemainder, message.citations, onCitationClick)}</div>
                            );
                          }
                          
                          if (tailParts.length > 0) {
                            parts.push(<div key="tail-with-alts">{tailParts}</div>);
                          }
                        } else {
                          // No alternatives in tail, process normally
                          parts.push(
                            <div key={`tail`}>{processMessageContent(tail, message.citations, onCitationClick)}</div>
                          );
                        }
                      }
                      return <div>{parts}</div>;
                    }
                  }

                  // ALTERNATIVES ANCHOR REPLACEMENT: Replace [ALTERNATIVES: anchor_id] markers with FilterAlternatives component
                  // CRITICAL: Support both scenarios:
                  // 1. message.filterAlternatives (attached to final message, like productDisplays)
                  // 2. alternativeDisplays prop (global state during streaming, like before)
                  const msgFilterAlts = (message as any).filterAlternatives;
                  const msgAltDisplays = (message as any).alternativeDisplays;
                  
                  const messageAlternativeDisplays = msgFilterAlts 
                    ? { [msgFilterAlts.anchorId]: msgFilterAlts }
                    : (msgAltDisplays || alternativeDisplays);
                    
                  console.log('🔍 ANCHOR CHECK:', {
                    messageId: message.id,
                    messageSender: message.sender,
                    hasMessageFilterAlternatives: !!msgFilterAlts,
                    hasMessageAltDisplays: !!msgAltDisplays,
                    hasPropAlternatives: !!alternativeDisplays,
                    filterAlternativesAnchorId: msgFilterAlts?.anchorId,
                    alternativeKeys: messageAlternativeDisplays ? Object.keys(messageAlternativeDisplays) : [],
                    messageContentLength: message.content.length,
                    messageContentPreview: message.content.substring(0, 300),
                    containsMarker: message.content.includes('[ALTERNATIVES:'),
                    markerMatch: message.content.match(/\[ALTERNATIVES:\s*([^\]]+)\]/g),
                    markerCount: (message.content.match(/\[ALTERNATIVES:/g) || []).length
                  });
                  
                  if (messageAlternativeDisplays && Object.keys(messageAlternativeDisplays).length > 0) {
                    console.log('✅ ALTERNATIVES REPLACEMENT STARTING:', {
                      messageId: message.id,
                      alternativeKeys: Object.keys(messageAlternativeDisplays),
                      contentSnippet: message.content.substring(0, 500),
                      hasMarker: message.content.includes('[ALTERNATIVES:')
                    });
                    
                    const alternativesRegex = /\[ALTERNATIVES:\s*([^\]]+)\]/g;
                    let idx = 0;
                    const parts: React.ReactNode[] = [];
                    let last = 0;
                    let m: RegExpExecArray | null;
                    
                    while ((m = alternativesRegex.exec(message.content)) !== null) {
                      console.log('🔍 Found alternatives anchor:', m[0], 'anchor_id:', m[1].trim());
                      const pre = message.content.slice(last, m.index);
                      if (pre) {
                        parts.push(
                          <div key={`alt-pre-${idx}`}>{processMessageContent(pre, message.citations, onCitationClick)}</div>
                        );
                      }
                      
                      const anchorId = m[1].trim();
                      const altData = messageAlternativeDisplays[anchorId];
                      
                      if (altData && altData.alternatives && altData.alternatives.length > 0) {
                        // Render FilterAlternatives component
                        parts.push(
                          <div key={`alt-${idx}`} className="my-4">
                            <FilterAlternatives
                              unmappedFilter={altData.unmappedFilter}
                              unmappedValue={altData.unmappedValue}
                              alternatives={altData.alternatives}
                              onSelect={(filters, description) => {
                                // Send natural language query - let orchestrator parse it
                                // System should use sub_class_description to search, then provide smart clarifying questions
                                onSendMessage(`Show me: ${description}`);
                              }}
                            />
                          </div>
                        );
                      }
                      
                      last = m.index + m[0].length;
                      idx++;
                    }
                    
                    const tail = message.content.slice(last);
                    if (parts.length > 0) {
                      if (tail) {
                        parts.push(
                          <div key={`alt-tail`}>{processMessageContent(tail, message.citations, onCitationClick)}</div>
                        );
                      }
                      console.log('✅ ALTERNATIVES REPLACED - Returning', parts.length, 'parts');
                      return <div>{parts}</div>;
                    } else {
                      console.log('⚠️ No alternatives anchors matched in content');
                    }
                  } else {
                    if (message.content.includes('[ALTERNATIVES:')) {
                      console.log('❌ ALTERNATIVES NOT REPLACED - No alternative displays available:', {
                        messageId: message.id,
                        hasMessageFilterAlts: !!msgFilterAlts,
                        hasMessageAltDisplays: !!msgAltDisplays,
                        hasPropAlternatives: !!alternativeDisplays
                      });
                    }
                  }

                  // Detect one or more JSON product_display blocks and render components inline (legacy fallback)
                  const codeBlockRegex = /```json\s*([\s\S]*?)\s*```/g;
                  const segments: React.ReactNode[] = [];
                  let lastIndex = 0;
                  let match: RegExpExecArray | null;
                  let foundAnyProductDisplay = false;

                  while ((match = codeBlockRegex.exec(message.content)) !== null) {
                    const [fullBlock, jsonPayload] = match;
                    // Preceding text segment
                    const preText = message.content.slice(lastIndex, match.index);
                    if (preText) {
                      segments.push(
                        <div key={`text-pre-${match.index}`} className="mb-3">
                          {processMessageContent(preText, message.citations, onCitationClick)}
                        </div>
                      );
                    }

                    try {
                      const parsed: any = JSON.parse(jsonPayload);
                      if (parsed && parsed.type === 'product_display' && Array.isArray(parsed.products)) {
                        // Do not render grouped product cards from JSON blocks; rely on SSE anchor replacement only.
                        foundAnyProductDisplay = true;
                        if (isProductAgent) {
                          setMessageLoading(message.id);
                        }
                        // Intentionally no segments.push here
                      } else {
                        // Non product_display JSON block - render as code
                        segments.push(
                          <pre key={`code-${match.index}`} className="markdown-code-block"><code>{fullBlock}</code></pre>
                        );
                      }
                    } catch {
                      // Invalid JSON, render as-is
                      segments.push(
                        <pre key={`code-invalid-${match.index}`} className="markdown-code-block"><code>{fullBlock}</code></pre>
                      );
                    }

                    lastIndex = match.index + fullBlock.length;
                  }

                  // Trailing text after last block
                  const postText = message.content.slice(lastIndex);
                  if (postText) {
                    segments.push(
                      <div key={`text-post-${lastIndex}`} className={foundAnyProductDisplay ? 'mt-3' : ''}>
                        {processMessageContent(postText, message.citations, onCitationClick)}
                      </div>
                    );
                  }

                  if (segments.length > 0 && foundAnyProductDisplay) {
                    return <div>{segments}</div>;
                  }

                  // If streaming and not complete, try to render any complete JSON blocks inline and keep streaming the rest
                  if (isCurrentlyStreaming && !message.isCompleted) {
                    const fullStreaming = (currentStreamingMessage || message.content);

                    // Detect an incomplete trailing JSON block and exclude it from inline rendering
                    const lastOpen = fullStreaming.lastIndexOf('```json');
                    const lastClose = fullStreaming.lastIndexOf('```');
                    const hasIncompleteTrailing = lastOpen !== -1 && (lastClose === -1 || lastOpen > lastClose);
                    const safeContent = hasIncompleteTrailing ? fullStreaming.slice(0, lastOpen) : fullStreaming;
                    const remainder = fullStreaming.slice(safeContent.length);

                    // Render any complete product_display blocks found in safeContent
                    // NOTE: Keep this lightweight; avoid heavy anchor matching during streaming
                    const codeBlockRegexStream = /```json\s*([\s\S]*?)\s*```/g;
                    const streamSegments: React.ReactNode[] = [];
                    let sLast = 0; let m: RegExpExecArray | null; let renderedAny = false;
                    while ((m = codeBlockRegexStream.exec(safeContent)) !== null) {
                      const [fullBlock, jsonPayload] = m;
                      const pre = safeContent.slice(sLast, m.index);
                      if (pre) {
                        streamSegments.push(
                          <div key={`s-pre-${m.index}`} className="mb-3">
                            {processMessageContent(pre, message.citations, onCitationClick)}
                          </div>
                        );
                      }
                      try {
                        const parsed: any = JSON.parse(jsonPayload);
                        if (parsed && parsed.type === 'product_display' && Array.isArray(parsed.products)) {
                          // Do not render grouped product cards during streaming; rely on SSE anchor replacement only.
                          renderedAny = true;
                          if (isProductAgent) { setMessageLoading(message.id); }
                          // Intentionally skip adding a visual segment
                        } else {
                          streamSegments.push(<pre key={`s-code-${m.index}`} className="markdown-code-block"><code>{fullBlock}</code></pre>);
                        }
                      } catch {
                        streamSegments.push(<pre key={`s-code-invalid-${m.index}`} className="markdown-code-block"><code>{fullBlock}</code></pre>);
                      }
                      sLast = m.index + fullBlock.length;
                    }
                    const sPost = safeContent.slice(sLast);
                    if (sPost) {
                      streamSegments.push(
                        <div key={`s-post-${sLast}`} className={renderedAny ? 'mt-3' : ''}>
                          {processMessageContent(sPost, message.citations, onCitationClick)}
                        </div>
                      );
                    }

                    const isCurrentMessageLoading = isMessageLoading(message.id);
                    return (
                      <>
                        {streamSegments.length > 0 ? <div>{streamSegments}</div> : null}
                        {remainder && (
                        <StreamingText 
                            content={remainder}
                          isComplete={false}
                          className="streaming-message-content break-words"
                          imageMaxWidth={imageMaxWidth}
                        />
                        )}
                        {isProductAgent && isCurrentMessageLoading && (
                          <div className="mt-4 mb-2 p-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 flex items-center justify-center">
                            <div className="flex flex-col items-center">
                              <div className="flex items-center mb-2">
                                <svg className="animate-spin mr-3 h-5 w-5 text-teal-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span className="font-medium">Rendering product cards...</span>
                              </div>
                              <div className="text-xs text-gray-500">Product cards from Product Agent are being rendered</div>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  }

                  // Regular markdown rendering
                  // Never render grouped fallback cards; rely exclusively on anchor replacement
                  const hasSseProductsFinal = false;
                  return (
                    <div className="break-words overflow-wrap-anywhere">
                      {hasSseProductsFinal && (
                        <div className="product-cards-direct-container w-full mt-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
                            {productListForMessage.map((product: any, index: number) => {
                              const delayMultiplier = Math.min(index, 5);
                              const idForCard = (product.base_sku || product.partialSku_OPIM)
                                ? `product-card-${String(product.base_sku || product.partialSku_OPIM).toUpperCase().replace(/[^A-Z0-9]/g, '')}`
                                : `product-card-${(product.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`;
                              const thumb = (getProxiedImageUrl(sanitizeUrl(product.primary_image || (product.color_variants && product.color_variants[0]?.image_url) || undefined)) || getFallbackImageUrl(product.attributes?.category));
                              
                              // Extract category with priority and debug logging
                              const extractedCategory = product.attributes?.sub_category || 
                                                        product.attributes?.category || 
                                                        (product as any).sub_class_description ||
                                                        (product as any).class_description ||
                                                        'Other';
                              
                              // Debug: Log first product's category data to understand structure
                              if (index === 0) {
                                console.log('🏷️ Product category debug:', {
                                  productName: product.name,
                                  'attributes.sub_category': product.attributes?.sub_category,
                                  'attributes.category': product.attributes?.category,
                                  'top-level available fields': Object.keys(product).filter(k => k.includes('class') || k.includes('category')),
                                  extractedCategory,
                                  fullAttributes: product.attributes
                                });
                              }
                              
                              return (
                                <div
                                  key={product.base_sku || `${product.name}-${index}`}
                                  id={idForCard}
                                  className="product-card-wrapper"
                                  data-product-name={product.name}
                                  data-partial-sku={product.partialSku_OPIM || product.base_sku || ''}
                                  data-product-image={thumb}
                                  data-product-category={extractedCategory}
                                  style={{ animation: `fadeIn 0.3s ease-in-out ${100 * delayMultiplier}ms forwards`, opacity: 0 }}
                                >
                                  <ProductCard product={product} />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {processMessageContent(
                        message.content.replace(/```json[\s\S]*?```/g, ''),
                        message.citations,
                        onCitationClick
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
          
          {/* Query Details Section - Disabled for UAT/PROD (tool call report widget hidden from end users) */}
          
          {/* Message Controls */}
          {isAgentMessage ? (
            hasMetadataSections ? (
              <>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <div className="flex items-center">{metadataToggleButton}</div>
                  <div className="flex items-center gap-2 ml-auto">
                    {timestampNode}
                    {feedbackControls}
                  </div>
                </div>
                {isMetadataOpen && (
                  <div className="mt-3 space-y-2">
                    {metadataSections}
                  </div>
                )}
              </>
            ) : (
              <div className="mt-2 flex items-center justify-end gap-2">
                {timestampNode}
                {feedbackControls}
              </div>
            )
          ) : (
            <div className="mt-2 flex justify-end">
              {timestampNode}
            </div>
          )}
           
           {/* NOTE: Filter Alternatives now render inline via [ALTERNATIVES: anchor_id] markers */}
           {/* The anchor replacement logic is in the message content rendering above (lines 925-970) */}
        </div>
      </div>
    </div>
  );
});

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  agent,
  user,
  messages,
  onSendMessage,
  onBackToAgents,
  isAgentTyping = false,
  isStreaming = false,
  currentStreamingMessage = '',
  toolCalls = [],
  currentToolCall = null,
  onClearChat,
  onMessagesUpdate,
  onRefreshAgents,
  sessionId = 'default-session',
  userId = 'default-user',
  onRegenerateMessage,
  chatError,
  onClearError,
  isStreamingEnabled = true,
  onToggleStreaming,

  isAgentSelectedViaCard = false,
  // Landing page props
  LandingPageComponent,
  onLandingPageQuestion,
  recentConversations,
  onOpenConversation,
  // Mobile layout props
  onToggleSidebar,
  isSidebarOpen,
  productDisplays,
  alternativeDisplays,
  handleAlternativeSelect,
  availableAgents,
  userProfile
}) => {
  // Analytics and monitoring hooks
  const { trackUserEvent, trackChatMetric, trackError } = useMonitoring();
  const { trackEvent, trackPageView } = useApplicationInsights();
  
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [regeneratingMessageId, setRegeneratingMessageId] = useState<string | null>(null);
  // Citation modal state
  const [showCitationModal, setShowCitationModal] = useState(false);
    const [selectedCitation, setSelectedCitation] = useState<Citation | null>(null);
  // Each message has its own expanded/collapsed state for search summary/sources
  const [expandedMessageIds, setExpandedMessageIds] = useState<Set<string>>(new Set()); // Collapsed by default
  
  // Feedback modal state
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [pendingFeedback, setPendingFeedback] = useState<{
    messageId: string;
    feedback: 'positive' | 'negative';
    agentName: string;
    userMessage: string;
    agentMessage: string;
    notesOnly: boolean;
    existingTags?: string[];
    existingNotes?: string;
    allowFeedbackSelection?: boolean;
    allowUpdate?: boolean;
  } | null>(null);
  
  // Auto-scroll state management
  const [isAutoScrollPaused, setIsAutoScrollPaused] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  
  // Product card loading states
  const { setMessageLoading, setMessageLoaded, isMessageLoading } = useProductCardLoading();
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContentRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Track window focus to prevent unwanted auto-scroll on tab focus
  const isWindowFocusing = useRef(false);
  
  // Track recent streaming state to prevent snap-back when streaming ends
  const wasRecentlyStreaming = useRef(false);
  
  // Track if scroll position has been restored on mount
  const scrollPositionRestored = useRef(false);

  // Thumbs animation state - tracks which message IDs should animate and which thumb is glowing
  // Animation triggers 3 seconds after message completes with 40% chance
  const [animatingMessageIds, setAnimatingMessageIds] = useState<Set<string>>(new Set());
  const [glowingThumb, setGlowingThumb] = useState<Record<string, 'up' | 'down' | null>>({});
  const animationTriggeredRef = useRef<Set<string>>(new Set());

  // Determine if current agent is Product Agent (for UI tweaks)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isProductAgentId = !!(agent && (
    agent.id === 'product' ||
    agent.id === 'product_finder_assistant' ||
    (typeof agent.id === 'string' && agent.id.toLowerCase().includes('product'))
  ));

  const PRODUCT_SEARCHLIGHT_MODE_KEY = 'productSearchlightMode';
  const [productSearchlightMode, setProductSearchlightMode] = useState<ProductSearchlightMode>(() => {
    try {
      const stored = localStorage.getItem(PRODUCT_SEARCHLIGHT_MODE_KEY);
      return stored === 'collaborator' ? 'collaborator' : 'chat';
    } catch {
      return 'chat';
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(PRODUCT_SEARCHLIGHT_MODE_KEY, productSearchlightMode);
    } catch {
      /* ignore */
    }
  }, [productSearchlightMode]);

  // Build-time feature flag — when off, Collaborator mode is fully disabled
  // (toggle hidden and mode forced to Chat even if localStorage has it set).
  const collaboratorFeatureEnabled = isFeatureEnabled('productCollaborator');

  const isCollaboratorMode =
    collaboratorFeatureEnabled && isProductAgentId && isAgentSelectedViaCard && productSearchlightMode === 'collaborator';

  const collaboratorRef = useRef<ProductCollaboratorViewHandle>(null);

  const handleClearChat = useCallback(() => {
    if (isCollaboratorMode) {
      collaboratorRef.current?.resetSession();
    }
    onClearChat?.();
  }, [isCollaboratorMode, onClearChat]);

  // Floating product tabs state (on by default, persists)
  const [showFloatingTabs, setShowFloatingTabs] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem('showFloatingProductTabs');
      return v === null ? true : v === 'true';
    } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('showFloatingProductTabs', String(showFloatingTabs)); } catch {}
  }, [showFloatingTabs]);
  
  // Image scale settings (persists in localStorage via hook)
  const { imageScale, setImageScale, getMaxWidth } = useImageScale();
  
  // Fast mode setting (persists in localStorage via hook).
  // The UI toggle was removed when Fast mode was deprecated; the value still
  // flows to the backend for any users who toggled it on previously and have
  // the preference cached in localStorage. Defaults to false (Precise mode).
  const { fastMode } = useFastMode();
  
  // Feedback request popup (one-time on first visit, persists in localStorage)
  const { shouldShowFeedbackRequest, dismissFeedbackRequest } = useFeedbackRequest();
  
  // Enhanced floating products with category and pinned status
  const [floatingProducts, setFloatingProducts] = useState<Array<{ 
    id: string; 
    partialSku: string; 
    image?: string;
    category?: string;
    name?: string;
  }>>([]);
  // Pin/bookmark UI is Collaborator-only; kept empty here so the product
  // request payload shape stays stable in Chat mode.
  const [pinnedProducts] = useState<Set<string>>(new Set());
  const [hasProductCards, setHasProductCards] = useState<boolean>(false);
  const didAutoOpenTabs = useRef<boolean>(false);

  // Product orchestrator experiment toggle (persists in localStorage)
  const [useResponsesExperiment] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    try {
      const stored = window.localStorage.getItem('ab_product_responses');
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('ab_product_responses', String(useResponsesExperiment));
    } catch {}
  }, [useResponsesExperiment]);

  // Observe product card DOM updates and keep latest cards in tabs (avoid flicker)
  useEffect(() => {
    if (!chatContentRef.current) return;
    const rootEl = chatContentRef.current as Element;
    const io = new IntersectionObserver(() => {
      // Keep a rolling window of the latest product cards (max 12)
      const cardEls = Array.from(rootEl.querySelectorAll('.product-card-wrapper')) as HTMLElement[];
      setHasProductCards(cardEls.length > 0);
      const items: Array<{ id: string; partialSku: string; image?: string; category?: string; name?: string }> = [];
      const seen = new Set<string>();
      const MAX_FLOATING_PRODUCTS = 12;
      // Walk from newest to oldest and capture the latest 12 unique cards
      for (let i = cardEls.length - 1; i >= 0; i--) {
        const el = cardEls[i];
        const id = el.id;
        if (!id || seen.has(id)) continue;
        seen.add(id);
        const partialSku = el.getAttribute('data-partial-sku') || el.getAttribute('data-product-name') || '';
        const category = el.getAttribute('data-product-category') || undefined;
        const name = el.getAttribute('data-product-name') || '';
        
        items.push({ 
          id, 
          partialSku, 
          image: el.getAttribute('data-product-image') || undefined,
          category,  // Category from search index (used for fallback images)
          name
        });
        if (items.length >= MAX_FLOATING_PRODUCTS) break;
      }
      // Keep backend ordering (bestsellers already sorted first by ranker)
      setFloatingProducts(items);
      
      // Debug: Log unique categories found
      if (items.length > 0) {
        const uniqueCategories = Array.from(new Set(items.map(i => i.category).filter(Boolean)));
        console.log('📊 Product categories extracted:', uniqueCategories);
      }

      // Auto-open once when cards first appear
      if (cardEls.length > 0 && !didAutoOpenTabs.current) {
        didAutoOpenTabs.current = true;
        setShowFloatingTabs(true);
      }
    }, { root: rootEl, threshold: 0 });

    const observeAllCards = () => {
      const cards = Array.from(rootEl.querySelectorAll('.product-card-wrapper')) as HTMLElement[];
      setHasProductCards(cards.length > 0);
      cards.forEach(el => io.observe(el));
    };

    // Observe current and future cards
    observeAllCards();
    const mo = new MutationObserver(() => observeAllCards());
    mo.observe(rootEl, { childList: true, subtree: true });

    return () => { io.disconnect(); mo.disconnect(); };
  }, [messages.length, isStreaming, chatContentRef]);

  // Feedback hooks
  const { submitFeedback, getFeedbackForMessage, clearFeedbacks } = useFeedback();
  const { getUserEmail, hasAnyRole, isSuperAdmin, hasAdminAccess, isAgentAdmin } = useAuth();
  const [whatsNewModalOpen, setWhatsNewModalOpen] = useState(false);
  
  // Check for newly completed messages and maybe trigger thumbs animation
  useEffect(() => {
    const completedAgentMessages = messages.filter(
      m => m.sender === 'agent' && 
           m.isCompleted === true && 
           !m.isStatusMessage &&
           !animationTriggeredRef.current.has(m.id)
    );
    
    completedAgentMessages.forEach(message => {
      // Mark as processed so we don't trigger again
      animationTriggeredRef.current.add(message.id);
      
      // 40% chance to trigger animation after 3 seconds
      if (Math.random() < 0.40) {
        setTimeout(() => {
          // Only animate if still no feedback
          const feedback = getFeedbackForMessage(message.id);
          if (!feedback) {
            setAnimatingMessageIds(prev => {
              const next = new Set(Array.from(prev));
              next.add(message.id);
              return next;
            });
            
            // Phase 1: Thumbs up glows (0 - 1s)
            setGlowingThumb(prev => ({ ...prev, [message.id]: 'up' }));
            
            // Phase 2: Thumbs down glows (1s - 2s)
            setTimeout(() => {
              setGlowingThumb(prev => ({ ...prev, [message.id]: 'down' }));
            }, 1200);
            
            // Remove animation after both thumbs finish (2s total)
            setTimeout(() => {
              setAnimatingMessageIds(prev => {
                const next = new Set(Array.from(prev));
                next.delete(message.id);
                return next;
              });
              setGlowingThumb(prev => ({ ...prev, [message.id]: null }));
            }, 2400);
          }
        }, 3000);
      }
    });
  }, [messages, getFeedbackForMessage]);
  
  // Check if user can access experimental product orchestrator
  const canAccessExperimentalOrchestrator = useMemo(() => {
    // Check RBAC status
    const rbacEnabled = process.env.REACT_APP_ENABLE_ENTRA_RBAC === 'true';
    
    // Allow if RBAC is off OR user has admin role
    if (!rbacEnabled) return true;
    
    return isSuperAdmin() || hasAnyRole(['admin', 'super-admin', 'product-admin', 'askaldo-admin']);
  }, [isSuperAdmin, hasAnyRole]);
  

  // Combined loading state - either typing new message or streaming
  const isAnyAgentActivity = isAgentTyping || isStreaming;
  
  // Removed duplicate scrollToBottom implementation - using consolidated version below

  // Handle feedback submission with ENHANCED v2 data
  const handleFeedbackSubmit = useCallback(async (
    messageId: string,
    feedback: 'positive' | 'negative',
    agentName: string,
    userMessage: string,
    agentMessage: string,
    feedbackTags?: string[],
    feedbackNotes?: string,
    allowUpdate?: boolean
  ) => {
    const userEmail = getUserEmail();
    const messageCount = messages.filter(m => m.sender === 'user').length;
    
    // Extract tool call names from the current toolCalls state and A2A tool calls
    const regularToolCallNames = toolCalls.map(tc => tc.name);
    const messageWithFeedback = messages.find(m => m.id === messageId);
    const a2aToolCallNames = messageWithFeedback?.a2aToolCalls || [];
    
    // Combine regular tool calls and A2A tool calls
    const allToolCallNames = [...regularToolCallNames, ...a2aToolCallNames];
    const a2aRouting = messageWithFeedback?.a2aRouting;
    
    // ===== NEW: Collect ENHANCED v2 data =====
    
    // 1. Prepare conversation history (all messages in the conversation)
    const conversationHistory = messages.map(msg => ({
      id: msg.id,
      sender: msg.sender,
      content: msg.content,
      timestamp: msg.timestamp.toISOString(), // Convert Date to ISO string
      agentName: msg.agentName,
      isCompleted: msg.isCompleted,
      toolCalls: msg.a2aToolCalls || [], // A2A tool calls from message
      a2aRouting: msg.a2aRouting,
      metadata: msg.metadata || undefined // Include metadata (e.g., products returned)
    }));
    
    // 2. Prepare detailed tool calls with parameters and results
    // Map from ToolCall (useChat) to ToolCallDetail (feedbackService)
    const toolCallsDetailed = toolCalls.map(tc => ({
      id: tc.id,
      name: tc.name,
      arguments: tc.input,           // input -> arguments
      result: tc.output,              // output -> result
      timestamp: tc.timestamp.toISOString(), // Convert Date to ISO string
      duration_ms: undefined,         // Not tracked in current ToolCall
      status: (tc.status === 'completed' ? 'success' : tc.status === 'error' ? 'error' : 'pending') as 'success' | 'error' | 'pending',
      error: tc.status === 'error' ? 'Tool call failed' : undefined
    }));
    
    // 3. Extract generic metadata from the message (e.g., product SKUs, agent-specific data)
    const messageMetadata = messageWithFeedback?.metadata || undefined;
    
    console.log('📊 Submitting v2 feedback with enhanced data:', {
      conversationMessages: conversationHistory.length,
      toolCallsDetailed: toolCallsDetailed.length,
      hasMetadata: !!messageMetadata,
      metadata: messageMetadata,
      messageId
    });
    
    // ===== END v2 data collection =====
    
    await submitFeedback(
      messageId,
      feedback,
      agentName,
      userMessage,
      agentMessage,
      userEmail,
      messageCount,
      allToolCallNames,
      a2aRouting,
      feedbackTags,
      feedbackNotes,
      conversationHistory,    // NEW: Full conversation
      toolCallsDetailed,      // NEW: Detailed tool calls
      messageMetadata,        // NEW: Generic metadata (product SKUs, etc.)
      allowUpdate
    );
  }, [submitFeedback, getUserEmail, messages, toolCalls]);
  
  // Add openModal method to handleFeedbackSubmit for child components
  (handleFeedbackSubmit as any).openModal = useCallback((data: any) => {
    setPendingFeedback(data);
    setShowFeedbackModal(true);
  }, []);

  // Clear feedbacks when chat is cleared
  React.useEffect(() => {
    if (messages.length === 0) {
      clearFeedbacks();
    }
  }, [messages.length, clearFeedbacks]);
  
  // Removed duplicate auto-scroll effect - handled by consolidated implementation below

  // Track page view when component mounts or agent changes
  React.useEffect(() => {
    // Clear any previous errors when switching agents
    if (chatError && onClearError) {
      onClearError();
    }
    
    if (agent) {
      trackPageView(`Chat - ${agent.name}`, undefined, {
        agentId: agent.id,
        agentName: agent.name,
        messageCount: messages.length,
        isStreaming: isStreaming,
        sessionId: sessionId,
        userId: userId
      });
      
      trackUserEvent({
        type: 'page_view',
        details: {
          page: 'chat_interface',
          agentId: agent.id,
          agentName: agent.name,
          messageCount: messages.length
        },
        timestamp: new Date(),
        userId: userId,
        sessionId: sessionId
      });
    }
  }, [agent?.id, trackPageView, trackUserEvent, userId, sessionId, chatError, onClearError]); // Only track when agent ID changes

  // Track errors when chatError changes (but only once per error)
  const lastTrackedErrorRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (chatError && chatError !== lastTrackedErrorRef.current) {
      lastTrackedErrorRef.current = chatError;
      
      trackError({
        message: chatError,
        component: 'ChatInterface',
        timestamp: new Date(),
        userId: userId,
        sessionId: sessionId,
        metadata: {
          agentId: agent?.id,
          agentName: agent?.name,
          messageCount: messages.length,
          isStreaming: isStreaming
        }
      });

      trackEvent('ChatError', {
        error: chatError,
        agentId: agent?.id || 'unknown',
        agentName: agent?.name || 'unknown',
        sessionId: sessionId,
        userId: userId,
        messageCount: messages.length.toString()
      });
    } else if (!chatError && lastTrackedErrorRef.current) {
      // Reset when error is cleared
      lastTrackedErrorRef.current = null;
    }
  }, [chatError, trackError, trackEvent, agent?.id, agent?.name, userId, sessionId]);

  // Separate static messages from streaming message for better performance
  const staticMessages = React.useMemo(() => {
    let filtered = messages.filter(m => m.sender === 'user' || m.sender === 'agent');
    
    // If currently streaming, filter out the last agent message if it's being streamed
    if (isStreaming && currentStreamingMessage) {
      // Find the last agent message and check if it's likely the streaming message
      let lastAgentMessageIndex = -1;
      for (let i = filtered.length - 1; i >= 0; i--) {
        if (filtered[i].sender === 'agent') {
          lastAgentMessageIndex = i;
          break;
        }
      }
      
      if (lastAgentMessageIndex !== -1) {
        const lastAgentMessage = filtered[lastAgentMessageIndex];
        // Remove if it's empty or contains the streaming content (indicates it's the streaming message)
        if (lastAgentMessage.content === '' || 
            lastAgentMessage.content === currentStreamingMessage ||
            currentStreamingMessage.startsWith(lastAgentMessage.content)) {
          filtered = filtered.filter((_, index) => index !== lastAgentMessageIndex);
        }
      }
    }
    
    return filtered;
  }, [messages, isStreaming, currentStreamingMessage]);

  // Combine static messages with status messages and streaming message
  const conversationHistory = React.useMemo(() => {
    const history = [...staticMessages];
    

    
    // Add streaming message if present
    if (isStreaming && currentStreamingMessage) {
      // Find the most recent agent message to preserve a2aRouting data (use messages, not staticMessages)
      const agentMessages = messages.filter(msg => msg.sender === 'agent');
      const lastAgentMessage = agentMessages[agentMessages.length - 1];
      
      console.log('🎬 STREAMING UI: Creating streaming message', {
        hasLastAgentMessage: !!lastAgentMessage,
        lastAgentMessageHasRouting: !!lastAgentMessage?.a2aRouting,
        lastAgentMessageRouting: lastAgentMessage?.a2aRouting,
        totalMessagesCount: messages.length,
        agentMessagesCount: agentMessages.length,
        messagesWithRouting: messages.filter(msg => msg.a2aRouting).length
      });
      
      history.push({
        id: 'streaming-message',
        content: currentStreamingMessage,
        sender: 'agent',
        timestamp: new Date(),
        isStreaming: true,
        agentName: agent?.name || 'Agent',
        wasStreamed: true,
        isCompleted: false,
        // CRITICAL: Include product displays so [PRODUCT_CARDS: anchor] markers can be replaced during streaming
        productDisplays: productDisplays,
        // GENERIC: Include alternative displays so [ALTERNATIVES: anchor] markers can be replaced during streaming
        alternativeDisplays: alternativeDisplays,
        // Preserve a2aRouting from the actual message during streaming
        ...(lastAgentMessage?.a2aRouting && { a2aRouting: lastAgentMessage.a2aRouting })
      } as any);
    }
    
    return history;
  }, [staticMessages, messages, isStreaming, currentStreamingMessage, agent?.name, productDisplays, alternativeDisplays]);

  // Simple scroll detection function - wrapped in useCallback for stable reference  
  const checkScrollPosition = useCallback(() => {
    if (!chatContentRef.current) return true;
    
    const element = chatContentRef.current;
    const threshold = 100;
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    return distanceFromBottom < threshold;
  }, []); // No dependencies needed - only uses DOM APIs

  // Simple scroll to bottom function - wrapped in useCallback for stable reference
  const scrollToBottom = useCallback((force = false) => {
    // Prevent auto-scroll during window focus events (fixes tab-back unwanted scrolling)
    if (!force && isWindowFocusing.current) {
      return;
    }
    
    if (force) {
      // Force scroll and reset pause state
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setIsAutoScrollPaused(false);
      setShowScrollToBottom(false);
    } else if (!isAutoScrollPaused) {
      // Normal auto-scroll only if not paused.
      // Immediate rather than smooth: a smooth scroll can fight a user's own
      // wheel or touch gesture while images below are still resolving, which
      // reads as the view snapping back up.
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }
  }, [isAutoScrollPaused]); // Include isAutoScrollPaused dependency

  // Handle scroll events - wrapped in useCallback for stable reference
  const handleScroll = useCallback(() => {
    if (!chatContentRef.current) return;
    
    const isAtBottom = checkScrollPosition();

    if (isStreaming) {
      if (!isAtBottom && !isAutoScrollPaused) {
        // User scrolled up during streaming - pause auto-scroll
        setIsAutoScrollPaused(true);
        setShowScrollToBottom(true);
      } else if (isAtBottom && isAutoScrollPaused) {
        // User scrolled back to bottom - resume auto-scroll
        setTimeout(() => {
          setIsAutoScrollPaused(false);
          setShowScrollToBottom(false);
        }, 300);
      }
    }
    
    // Always show button when not at bottom (regardless of streaming state)
    setShowScrollToBottom(!isAtBottom);
  }, [isStreaming, isAutoScrollPaused, checkScrollPosition]); // Include all dependencies

  // Auto-scroll when messages change, but respect user scroll position during streaming transitions
  useEffect(() => {
    // Don't auto-scroll if we're in the process of restoring scroll position
    // This prevents the immediate snap to bottom after page refresh/reload
    if (!scrollPositionRestored.current && messages.length > 0) {
      // Wait a bit for scroll restoration to complete before enabling auto-scroll
      return;
    }

    // Track if we were recently streaming to avoid snapping when streaming ends
    if (isStreaming) {
      wasRecentlyStreaming.current = true;
    }
    
    // If we're not streaming anymore but were recently streaming, preserve user position
    if (!isStreaming && wasRecentlyStreaming.current) {
      wasRecentlyStreaming.current = false;
      // Only scroll if user is NOT paused (meaning they never scrolled up during streaming)
      if (!isAutoScrollPaused) {
        scrollToBottom();
      }
      // If auto-scroll was paused, do NOT scroll - preserve user position
    } else if (!wasRecentlyStreaming.current && scrollPositionRestored.current) {
      // Normal scroll behavior for regular message additions (when not transitioning from streaming)
      // Only auto-scroll after position has been restored (or if there was nothing to restore)
      scrollToBottom();
    }
  }, [messages, scrollToBottom, isStreaming, isAutoScrollPaused]);

  // Auto-scroll during streaming
  useEffect(() => {
    if (isStreaming && currentStreamingMessage && !isAutoScrollPaused) {
      scrollToBottom();
    }
  }, [currentStreamingMessage, isStreaming, isAutoScrollPaused, scrollToBottom]);

  // Note: Pause state reset is now handled by the scroll event handler when user manually scrolls back to bottom

  // Set up scroll event listener
  useEffect(() => {
    const chatContent = chatContentRef.current;
    if (!chatContent) return;

    // Capture the current timeout ref value at effect setup to avoid stale closure
    const currentScrollTimeoutRef = scrollTimeoutRef.current;

    chatContent.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      chatContent.removeEventListener('scroll', handleScroll);
      if (currentScrollTimeoutRef) {
        clearTimeout(currentScrollTimeoutRef);
      }
    };
  }, [handleScroll]);

  // Prevent auto-scroll when window is gaining focus (fixes tab-back scroll issue)
  useEffect(() => {
    const handleWindowFocus = () => {
      isWindowFocusing.current = true;
      // Reset the flag after a short delay
      setTimeout(() => {
        isWindowFocusing.current = false;
      }, 500);
    };

    const handleWindowBlur = () => {
      isWindowFocusing.current = false;
    };

    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('blur', handleWindowBlur);
    
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, []);

  // Save scroll position to sessionStorage to restore on page refresh
  useEffect(() => {
    const chatContent = chatContentRef.current;
    if (!chatContent || !agent) return;

    // Create a unique key for this conversation's scroll position
    const scrollKey = `chat-scroll-${agent.id}-${sessionId}`;

    // Save scroll position periodically while scrolling
    const saveScrollPosition = () => {
      try {
        const scrollData = {
          scrollTop: chatContent.scrollTop,
          timestamp: Date.now()
        };
        sessionStorage.setItem(scrollKey, JSON.stringify(scrollData));
      } catch (error) {
        // Silently fail if sessionStorage is not available
        console.warn('Failed to save scroll position:', error);
      }
    };

    // Debounced scroll position save
    let saveTimeout: NodeJS.Timeout;
    const handleScrollSave = () => {
      clearTimeout(saveTimeout);
      saveTimeout = setTimeout(saveScrollPosition, 200);
    };

    // Save before page unload
    const handleBeforeUnload = () => {
      saveScrollPosition();
    };

    chatContent.addEventListener('scroll', handleScrollSave, { passive: true });
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearTimeout(saveTimeout);
      chatContent.removeEventListener('scroll', handleScrollSave);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Save one final time on cleanup
      saveScrollPosition();
    };
  }, [agent, sessionId]);

  // Restore scroll position on mount or when messages load
  useEffect(() => {
    const chatContent = chatContentRef.current;
    if (!chatContent || !agent || scrollPositionRestored.current) return;

    // Only restore if we have messages to scroll to
    if (messages.length === 0) return;

    const scrollKey = `chat-scroll-${agent.id}-${sessionId}`;

    try {
      const savedScrollData = sessionStorage.getItem(scrollKey);
      if (savedScrollData) {
        const { scrollTop, timestamp } = JSON.parse(savedScrollData);
        
        // Only restore if the data is recent (within last hour)
        const isRecent = Date.now() - timestamp < 3600000;
        
        if (isRecent && scrollTop > 0) {
          // Wait for messages to render, then restore scroll position
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              chatContent.scrollTop = scrollTop;
              scrollPositionRestored.current = true;
              console.log('✅ Restored scroll position:', scrollTop);
            });
          });
        } else {
          // No valid saved position or expired - scroll to bottom to show latest messages
          scrollPositionRestored.current = true;
          requestAnimationFrame(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
            console.log('📜 Scrolled to bottom (no recent saved position)');
          });
        }
      } else {
        // No saved scroll position - scroll to bottom to show latest messages
        // This handles restored conversations and fresh page loads
        scrollPositionRestored.current = true;
        requestAnimationFrame(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
          console.log('📜 Scrolled to bottom (no saved position)');
        });
      }
    } catch (error) {
      console.warn('Failed to restore scroll position:', error);
      // On error, scroll to bottom as fallback
      scrollPositionRestored.current = true;
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      });
    }
  }, [agent, sessionId, messages.length]);

  // Reset scroll restoration flag when switching agents
  useEffect(() => {
    scrollPositionRestored.current = false;
  }, [agent?.id]);










  // Copy message to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Copy conversation to clipboard
  const copyConversationToClipboard = () => {
    const conversationText = messages
      .filter(msg => msg.sender !== 'system')
      .map(msg => `${msg.sender === 'user' ? 'You' : agent?.name || 'Agent'}: ${msg.content}`)
      .join('\n\n');
    
    copyToClipboard(conversationText);
  };

  // Handle suggestion clicks
  const handleSuggestionClick = (suggestion: string) => {
    // Track suggestion usage
    trackEvent('SuggestionClicked', {
      agentId: agent?.id || 'unknown',
      agentName: agent?.name || 'unknown',
      suggestion: suggestion.substring(0, 100), // First 100 chars for privacy
      sessionId: sessionId,
      userId: userId
    });
    
    handleSendMessageWithTracking(suggestion);
  };

  // Enhanced message sending with analytics tracking
  const handleSendMessageWithTracking = useCallback((content: string) => {
    const startTime = Date.now();
    
    // Clear any previous errors when sending a new message
    if (chatError && onClearError) {
      onClearError();
    }
    
    // Track message sending event
    trackUserEvent({
      type: 'message_send',
      details: {
        agentId: agent?.id || 'unknown',
        agentName: agent?.name || 'unknown',
        messageLength: content.length,
        messageType: 'user_input',
        hasAttachments: false, // Could be extended for file uploads
        isLongMessage: content.length > 500
      },
      timestamp: new Date(),
      userId: userId,
      sessionId: sessionId
    });

    // Track chat metric
    trackChatMetric('message_sent', {
      startTime: startTime,
      agentId: agent?.id,
      messageLength: content.length,
      isStreaming: isStreamingEnabled,
      hasError: false,
      userId: userId,
      sessionId: sessionId
    });

    // Track in Application Insights
    trackEvent('MessageSent', {
      agentId: agent?.id || 'unknown',
      agentName: agent?.name || 'unknown',
      messageLength: content.length.toString(),
      sessionId: sessionId,
      userId: userId,
      isLongMessage: (content.length > 500).toString()
    }, {
      messageLength: content.length,
      currentMessageCount: messages.length
    });

    // Cancel any in-flight image loads to avoid contention while starting a new stream
    try { cancelAllImagePreloads(); } catch {}

    const options: Record<string, unknown> = {};

    if (agent?.id === 'product' || agent?.id.includes('product')) {
      options.productContext = {
        pinnedProducts: Array.from(pinnedProducts),
        activeProducts: floatingProducts.map(p => ({
          sku: p.partialSku,
          category: p.category
        }))
      };
    }

    if (userProfile) {
      options.userContext = {
        displayName: userProfile.displayName,
        firstName: userProfile.firstName,
        lastName: userProfile.lastName,
        email: userProfile.email,
        jobTitle: userProfile.jobTitle,
        department: userProfile.department,
        officeLocation: userProfile.officeLocation,
        city: userProfile.city,
        state: userProfile.state,
        country: userProfile.country
      };
    }
    
    // Include fast mode preference
    options.fastMode = fastMode;
    
    onSendMessage(content, Object.keys(options).length > 0 ? options : undefined);
    
    // Auto-scroll to user message after sending
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 100);
  }, [onSendMessage, agent, userId, sessionId, isStreamingEnabled, messages.length, trackUserEvent, trackChatMetric, trackEvent, pinnedProducts, floatingProducts, userProfile, fastMode, chatError, onClearError]);

  // Handle citation clicks
  const handleCitationClick = (citation: Citation) => {
    setSelectedCitation(citation);
    setShowCitationModal(true);
  };

  // Close citation modal
  const closeCitationModal = () => {
    setShowCitationModal(false);
    setSelectedCitation(null);
  };

  // Handle regenerate
  const handleRegenerate = useCallback(async (messageId: string, additionalContext?: string) => {
    if (!agent || !onRegenerateMessage || !onMessagesUpdate) return;
    
    try {
      await onRegenerateMessage(
        messageId, 
        agent, 
        messages, 
        onMessagesUpdate, 
        isStreamingEnabled, 
        additionalContext
      );
      // Log removed
    } catch (error) {
      console.error('Regeneration failed:', error);
      // Error is already handled by the useChat hook and will be displayed
    }
  }, [agent, messages, onMessagesUpdate, onRegenerateMessage, isStreamingEnabled]);

  const handleRegenerateClick = useCallback((messageId: string) => {
    setRegeneratingMessageId(messageId);
    setShowRegenerateModal(true);
  }, []);

  const closeRegenerateModal = useCallback(() => {
    setShowRegenerateModal(false);
    setRegeneratingMessageId(null);
  }, []);

  // Get the original user prompt for the regenerate modal
  const getOriginalPrompt = useCallback((messageId: string): string => {
    const messageIndex = messages.findIndex(msg => msg.id === messageId);
    if (messageIndex === -1 || messages[messageIndex].sender !== 'agent') return '';
    
    const userMessageIndex = messageIndex - 1;
    if (userMessageIndex < 0 || messages[userMessageIndex].sender !== 'user') return '';
    
    return messages[userMessageIndex].content;
  }, [messages]);

  // Get the latest A2A routing information from messages
  const latestA2ARouting = useMemo(() => {
    // Find the most recent agent message with A2A routing
    const agentMessages = messages.filter(msg => msg.sender === 'agent');
    const latestAgentMessage = agentMessages[agentMessages.length - 1];
    const routing = latestAgentMessage?.a2aRouting || null;
    
    
    return routing;
  }, [messages]);

  // Helper functions for citation modal
  const getPolicyTechUrl = (filename: string): string | null => {
    const policyTechId = extractPolicyTechId(filename);
    return policyTechId ? `https://unifirst.policytech.com/docview/?docid=${policyTechId}` : null;
  };

  const extractPolicyTechId = (filename: string): string | null => {
    // Try the new format first: "Policy Name (ID).pdf"
    const match = filename.match(/\((\d+)\)/);
    if (match) {
      return match[1];
    }
    
    // Fallback to old PT- format if needed
    const ptMatch = filename.match(/PT-(\d+)/);
    return ptMatch ? ptMatch[1] : null;
  };

  // Get the source URL for a citation (supports UniFirst Docs source_url)
  const getSourceUrl = (citation: Citation): string | null => {
    // First check for direct source_url (UniFirst Docs)
    if (citation.source_url) {
      return citation.source_url;
    }
    // Also check metadata for source_url
    if (citation.metadata?.source_url) {
      return citation.metadata.source_url;
    }
    // Fallback to PolicyTech URL for HR policies
    return getPolicyTechUrl(citation.filename);
  };

  // Check if citation is a form document
  const isFormDocument = (citation: Citation): boolean => {
    return citation.is_form || citation.metadata?.is_form || false;
  };

  if (!agent) {
    // Show LandingPage if available, otherwise show "No agent selected" message
    if (LandingPageComponent && onLandingPageQuestion) {
      return (
        <div className="flex h-screen scout-canvas">
          <div className="landing-page-content">
            <LandingPageComponent
              onQuestionSubmit={onLandingPageQuestion}
              recentConversations={recentConversations}
              onOpenConversation={onOpenConversation}
            />
          </div>
        </div>
      );
    }
    
    // Fallback: always show landing page when no agent is selected
    return (
      <div className="flex h-screen scout-canvas">
        <div className="landing-page-content flex items-center justify-center p-8">
          <div className="text-center">
            <div className="text-6xl mb-4">
              <Bot className="h-24 w-24 inline text-teal-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Welcome to Scout Central</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-8">Your UniFirst AI Assistant is ready to help with HR questions, policies, and workplace guidance.</p>
            <button className="bg-teal-500 text-white px-6 py-3 rounded-xl font-medium hover:bg-teal-600 transition-colors" onClick={() => window.location.reload()}>
              <Home className="h-5 w-5 inline mr-2" />
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen scout-canvas">
      {/* Center Chat Pane */}
      <div className="main-chat">
        {/* Top Navigation */}
        <TopNavbar
          agent={agent}
          user={user}
          onBackToAgents={onBackToAgents}
          isAgentTyping={isAgentTyping}
          messages={messages}
          a2aRouting={latestA2ARouting}
          onClearChat={handleClearChat}
          onCopyConversation={copyConversationToClipboard}
          onToggleSidebar={onToggleSidebar}
          isSidebarOpen={isSidebarOpen}
          isDirectAgentAccess={isAgentSelectedViaCard}
          showProductSearchlightModeToggle={collaboratorFeatureEnabled && isProductAgentId && isAgentSelectedViaCard}
          productSearchlightMode={productSearchlightMode}
          onProductSearchlightModeChange={setProductSearchlightMode}
        />

        {isCollaboratorMode ? (
          <ProductCollaboratorView
            ref={collaboratorRef}
            imageScale={imageScale}
            fastMode={fastMode}
            agent={agent}
          />
        ) : (
        <>
        {/* Chat Messages */}
        <div className="chat-content" ref={chatContentRef}>
          <div className="w-full py-6 px-[2vw] max-w-full mx-auto">
            
            {/* Error Message */}
            {chatError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
                    <span className="text-sm text-red-600 dark:text-red-400">{chatError}</span>
                  </div>
                  {onClearError && (
                    <button
                      onClick={onClearError}
                      className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Messages */}
            <div className="space-y-4 w-full mx-auto">
              {conversationHistory.map((message, index) => (
                <ChatMessage
                  key={message.id}
                  message={message}
                  agent={agent}
                  user={user}
                  isAnyAgentActivity={isAnyAgentActivity}
                  isStreaming={isStreaming}
                  currentStreamingMessage={currentStreamingMessage}
                  onMessagesUpdate={onMessagesUpdate}
                  onRegenerateClick={handleRegenerateClick}
                  onCopyMessage={copyToClipboard}
                  onSuggestionClick={handleSuggestionClick}
                  onCitationClick={handleCitationClick}
                  onFeedbackSubmit={handleFeedbackSubmit}
                  messages={messages}
                  isWelcomeMessage={message.id === 'welcome-message'}
                  canEditWhatsNew={message.id === 'welcome-message' && !!agent && isAgentAdmin(agent.id)}
                  onEditWhatsNew={() => setWhatsNewModalOpen(true)}
                  showConversationStarters={messages.filter(m => m.sender === 'user').length === 0}
                  messageFeedback={(() => {
                    const feedback = getFeedbackForMessage(message.id);
                    if (!feedback) return undefined;
                    return {
                      positive: feedback.feedback === 'positive',
                      negative: feedback.feedback === 'negative'
                    };
                  })()}
                  isSourcesSectionExpanded={expandedMessageIds.has(message.id)}
                  onToggleSourcesSection={() => {
                    const newExpanded = new Set(expandedMessageIds);
                    if (newExpanded.has(message.id)) {
                      newExpanded.delete(message.id);
                    } else {
                      newExpanded.add(message.id);
                    }
                    setExpandedMessageIds(newExpanded);
                  }}
                  setMessageLoading={setMessageLoading}
                  setMessageLoaded={setMessageLoaded}
                  thumbsAnimating={animatingMessageIds.has(message.id)}
                  glowingThumb={glowingThumb[message.id] || null}
                  isMessageLoading={isMessageLoading}
                  productDisplays={productDisplays}
                  alternativeDisplays={alternativeDisplays}
                  onSendMessage={onSendMessage}
                  imageMaxWidth={getMaxWidth()}
                  imageScale={imageScale}
                />
              ))}
              
              <div ref={messagesEndRef} />
            </div>
          </div>
        </div>

        {/* Floating product "bookmark" tabs are intentionally omitted in Chat
            mode — product bookmarking lives only in Collaborator mode. */}

        {/* Scroll to Bottom Button - Removed for cleaner UI */}

        {/* Input Area */}
        <div className="input-area">
          <InputBox
            onSendMessage={handleSendMessageWithTracking}
            disabled={isAgentTyping}
            isStreaming={isStreaming}
            availableAgents={availableAgents}
            isLockedChat={isAgentSelectedViaCard}
          />
        </div>
        </>
        )}
      </div>



      {/* Regenerate Modal */}
      {regeneratingMessageId && (
        <RegenerateModal
          isOpen={showRegenerateModal}
          onClose={closeRegenerateModal}
          onRegenerate={(additionalContext) => handleRegenerate(regeneratingMessageId, additionalContext)}
          originalPrompt={getOriginalPrompt(regeneratingMessageId)}
          isLoading={isAnyAgentActivity}
        />
      )}

      {/* Feedback Modal */}
      {pendingFeedback && (
        <FeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => {
            setShowFeedbackModal(false);
            setPendingFeedback(null);
          }}
          onSubmit={async (tags, notes, feedbackOverride) => {
            const feedbackToUse = feedbackOverride ?? pendingFeedback.feedback;
            await handleFeedbackSubmit(
              pendingFeedback.messageId,
              feedbackToUse,
              pendingFeedback.agentName,
              pendingFeedback.userMessage,
              pendingFeedback.agentMessage,
              tags,
              notes,
              pendingFeedback.allowUpdate
            );
            setShowFeedbackModal(false);
            setPendingFeedback(null);
          }}
          notesOnly={pendingFeedback.notesOnly}
          allowedTags={['bugfix', 'wishlist feature', 'helpfulness', 'tone', 'correctness', 'safety']}
          defaultTags={pendingFeedback.existingTags}
          defaultNotes={pendingFeedback.existingNotes}
          showFeedbackSelector={pendingFeedback.allowFeedbackSelection}
          defaultFeedback={pendingFeedback.feedback}
        />
      )}

      {/* Feedback Request Modal - One-time popup asking users to use thumbs up/down */}
      <FeedbackRequestModal
        isOpen={shouldShowFeedbackRequest}
        onClose={dismissFeedbackRequest}
        onAcknowledge={dismissFeedbackRequest}
      />

      {/* What's New editor — inline admin affordance from the agent intro
          message. Agent-admins (and super-admins) can edit the dated blurb;
          the editor saves to agents.json via /admin/registry/:agentId/whats-new
          and is visible to super-admins in Management → Agents. */}
      {whatsNewModalOpen && agent && (
        <div
          className="citation-modal-overlay"
          onClick={() => setWhatsNewModalOpen(false)}
        >
          <div
            className="citation-modal"
            style={{ maxWidth: 760, width: '92vw' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="citation-modal-header">
              <h3 className="citation-modal-title">
                Agent intro update
              </h3>
              <button
                className="citation-modal-close"
                onClick={() => setWhatsNewModalOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div style={{ padding: '8px 4px 4px' }}>
              <AgentWhatsNewEditor
                agentId={agent.id}
                agentName={agent.name}
                initialNotice={agent.updateNotice}
                version={agent.version}
                onSaved={(notice) => {
                  // Instantly refresh the banner in the open intro message so
                  // the admin sees their edit without reloading the chat.
                  if (onMessagesUpdate) {
                    onMessagesUpdate(
                      messages.map((m) =>
                        m.id === 'welcome-message'
                          ? { ...m, content: applyWhatsNew(m.content, notice) }
                          : m
                      )
                    );
                  }
                  // Refresh the agent registry so the home-screen badge and
                  // Management view pick up the change from agents.json.
                  onRefreshAgents?.();
                  setWhatsNewModalOpen(false);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Citation Modal */}
      {showCitationModal && selectedCitation && (
        <div className="citation-modal-overlay" onClick={closeCitationModal}>
          <div className="citation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="citation-modal-header">
              <h3 className="citation-modal-title">
                <span className="citation-modal-icon">📄</span>
                Citation Details
              </h3>
              <button 
                className="citation-modal-close" 
                onClick={closeCitationModal}
              >
                ×
              </button>
            </div>
            
            <div className="citation-modal-content">

              <div className="citation-source">
                <strong>Source:</strong> 
                {(() => {
                  const sourceUrl = getSourceUrl(selectedCitation);
                  return sourceUrl ? (
                    <a 
                      href={sourceUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="policy-tech-link"
                    >
                      {selectedCitation.filename}
                    </a>
                  ) : (
                    <span>{selectedCitation.filename}</span>
                  );
                })()}
              </div>
              
              {/* Doc ID for UniFirst Docs */}
              {(selectedCitation.doc_id || selectedCitation.metadata?.doc_id) && (
                <div className="citation-doc-id">
                  <strong>Doc ID:</strong> {selectedCitation.doc_id || selectedCitation.metadata?.doc_id}
                </div>
              )}
              
              {/* Section (for UniFirst Docs) */}
              {(selectedCitation.section || selectedCitation.metadata?.section_h2) && (
                <div className="citation-section" style={{ marginTop: '4px' }}>
                  <strong>Section:</strong> {selectedCitation.section || selectedCitation.metadata?.section_h2}
                </div>
              )}
              
              {/* Page number (for HR Policies - only show if no section) */}
              {!selectedCitation.section && !selectedCitation.metadata?.section_h2 && selectedCitation.page_number && selectedCitation.page_number > 0 && (
                <div className="citation-page">
                  <strong>Page:</strong> {selectedCitation.page_number}
                </div>
              )}
              
              {selectedCitation.metadata?.state && (
                <div className="citation-state">
                  <strong>State:</strong> {selectedCitation.metadata.state}
                </div>
              )}

              {/* Form Document Indicator */}
              {isFormDocument(selectedCitation) && (
                <div className="citation-form-notice" style={{ 
                  backgroundColor: 'rgba(16, 185, 129, 0.1)', 
                  padding: '8px 12px', 
                  borderRadius: '6px', 
                  marginTop: '8px',
                  border: '1px solid rgba(16, 185, 129, 0.3)'
                }}>
                  <span style={{ fontSize: '16px', marginRight: '8px' }}>📋</span>
                  <strong>This is a form document.</strong> Click below to view/download on UniFirst Docs (opens in new tab).
                </div>
              )}
              
              {/* View Document Section (works for both PolicyTech and UniFirst Docs) */}
              {getSourceUrl(selectedCitation) && (
                <div className="policy-tech-section">
                  <div className="policy-tech-header">
                    <strong>{isFormDocument(selectedCitation) ? 'View Form:' : 'View Document:'}</strong>
                  </div>
                  <a 
                    href={getSourceUrl(selectedCitation)!} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="policy-tech-full-link"
                  >
                    <span className="policy-tech-icon">{isFormDocument(selectedCitation) ? '📄' : '📋'}</span>
                    {isFormDocument(selectedCitation) 
                      ? `Open ${selectedCitation.filename} on UniFirst Docs ↗`
                      : (selectedCitation.doc_id || selectedCitation.metadata?.doc_id) 
                        ? `View Full Document (Doc ID: ${selectedCitation.doc_id || selectedCitation.metadata?.doc_id}) ↗`
                        : extractPolicyTechId(selectedCitation.filename)
                          ? `View Full Document (ID: ${extractPolicyTechId(selectedCitation.filename)}) ↗`
                          : 'View Full Document ↗'
                    }
                  </a>
                </div>
              )}
              
              <div className="citation-content-section">
                {/* Display collated chunks if available, otherwise single content */}
                {selectedCitation.chunks && selectedCitation.chunks.length > 0 ? (
                  <>
                    <h4>Sections ({selectedCitation.chunks.length}):</h4>
                    <div className="citation-chunks-list" style={{ 
                      maxHeight: '400px', 
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}>
                      {selectedCitation.chunks.map((chunk, index) => (
                        <div 
                          key={index} 
                          className="citation-chunk-item"
                          style={{
                            backgroundColor: 'rgba(0,0,0,0.03)',
                            borderRadius: '8px',
                            padding: '12px',
                            border: '1px solid rgba(0,0,0,0.08)'
                          }}
                        >
                          {chunk.section && (
                            <div className="chunk-section-header" style={{
                              fontWeight: 600,
                              color: '#4f46e5',
                              marginBottom: '6px',
                              fontSize: '13px'
                            }}>
                              📌 {chunk.section}
                            </div>
                          )}
                          <div className="chunk-content" style={{
                            fontSize: '13px',
                            lineHeight: '1.5',
                            color: '#3d4045',
                            whiteSpace: 'pre-wrap'
                          }}>
                            {chunk.content}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : selectedCitation.content ? (
                  <>
                    <h4>Content:</h4>
                    <div className="citation-content">
                      {selectedCitation.content}
                    </div>
                  </>
                ) : (
                  <div style={{ color: '#9da2a8', fontStyle: 'italic' }}>
                    No content available. Click "View Document" to see the full document.
                  </div>
                )}
              </div>
              
              <div className="citation-actions">
                <button 
                  className="citation-copy-btn"
                  onClick={() => {
                    // For collated citations, copy all chunk contents
                    const contentToCopy = selectedCitation.chunks && selectedCitation.chunks.length > 0
                      ? selectedCitation.chunks.map((c, i) => `[${c.section || 'Section ' + (i+1)}]\n${c.content}`).join('\n\n---\n\n')
                      : selectedCitation.content || '';
                    copyToClipboard(contentToCopy);
                  }}
                >
                  📋 Copy Content
                </button>
                <button 
                  className="citation-copy-btn"
                  onClick={() => copyToClipboard(selectedCitation.citation)}
                >
                  📑 Copy Citation
                </button>
                {getSourceUrl(selectedCitation) && (
                  <button 
                    className="citation-copy-btn secondary"
                    onClick={() => copyToClipboard(getSourceUrl(selectedCitation)!)}
                  >
                    🔗 Copy Document Link
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Panel - Top Right */}
      <ImageScaleSettings 
        imageScale={imageScale}
        onImageScaleChange={setImageScale}
        showUserGuide={agent?.id === 'product'}
      />
    </div>
  );
};

export default ChatInterface; 